/**
 * Moments — as cenas-recompensa do reformu.lat, na gramática da vela/dossiê:
 * material temático (papel, brasa, lua), física de mola e uma pequena
 * narrativa em cada uma.
 *
 * Cerimônias: vela do dia · level up · amanhecer (100%) · noite lendária ·
 * marcos de ofensiva (candelabro) · vigília (25min) · livro nasce · marcos de
 * travessia (lua) · lacre da exportação · ficha arquivada. Micro: brasinha da
 * primeira palavra.
 *
 * <MomentsWatcher/> detecta gatilhos com dados reais (guardas em localStorage
 * + baseline silencioso no 1º uso) e também escuta o evento
 * `reformu:moment` — usado pelos fluxos (export, universo) e pelo atalho de
 * teste `playMoment('<cena>')` no console. Respeita prefers-reduced-motion.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'motion/react';
import { useLocation } from 'react-router-dom';
import { useAppStore } from '../stores/app-store';
import { totalWordsOf, writerLevelOf, writerTitleOf } from '../lib/writer-level';
import { useSubscriptionStore, type PlanType } from '../stores/subscription-store';
import { PlanFreeMoment, PlanStarterMoment, PlanProfessionalMoment, PlanUnlimitedMoment, HiredMoment } from './moments-plans';
import { NotifCardMoment } from './moments-notifs';
import { ComboMoment, FlowMoment, BurstMoment, DeepFlowMoment, TranceMoment, MythicNightMoment, AuroraNightMoment, IrisMoment, FirefliesMoment } from './moments-live';
import { TwoQuillsMoment, CompanionMoment, TimeBackMoment, CleanPageMoment, GoalSetMoment, BaptismMoment, BookNamedMoment, PhoenixMoment, PruneMoment, EarlyBirdMoment, TwoTalesMoment, ThreeVigilsMoment, ArchiveFullMoment, WishMoment, ThousandMoment } from './moments-story';

const DAILY_GOAL = 500;
const LEGENDARY_GOAL = 1000;
const NOVEL_TARGET = 80_000;
const VIGIL_MINUTES = 25;

type Moment =
  | { type: 'candle' }
  | { type: 'level'; level: number }
  | { type: 'dawn'; title: string }
  | { type: 'spark' }
  | { type: 'legendary' }
  | { type: 'streak'; nights: number }
  | { type: 'vigil' }
  | { type: 'born'; title: string }
  | { type: 'crossing'; pct: number; title: string }
  | { type: 'seal'; title: string }
  | { type: 'file'; name: string; label: string }
  | { type: 'burn'; name: string }
  | { type: 'unbind'; title: string }
  | { type: 'twin'; title: string }
  | { type: 'coverswap'; title: string; img?: string }
  | { type: 'feather' }
  | { type: 'fullweek' }
  | { type: 'inkdry' }
  | { type: 'letter' }
  | { type: 'tensouls' }
  | { type: 'midnight' }
  | { type: 'lastcall' }
  | { type: 'bookmark' }
  | { type: 'lantern'; title: string }
  | { type: 'flourish' }
  | { type: 'weave' }
  | { type: 'fullmoon' }
  | { type: 'oath' }
  | { type: 'shelffull'; count: number }
  | { type: 'storm' }
  | { type: 'return' }
  | { type: 'tearpage' }
  | { type: 'firstword'; title: string }
  | { type: 'scroll'; name: string }
  | { type: 'newmoon' }
  | { type: 'hundredk' }
  | { type: 'spine10'; title: string }
  | { type: 'record'; words: number }
  | { type: 'monthstars'; nights: number }
  | { type: 'cartographer' }
  | { type: 'fiftycandles' }
  | { type: 'reveal'; name: string; img?: string }
  | { type: 'paperlight' }
  | { type: 'arrived'; title: string }
  | { type: 'plan'; plan: PlanType }
  | { type: 'hired'; name: string }
  | { type: 'notif'; kind: 'invite' | 'mention' | 'review' | 'hire' | 'system' }
  | { type: 'combo'; tier: 1 | 2 | 3 | 4 | 5; words: number }
  | { type: 'flow' }
  | { type: 'burst'; tier: 1 | 2 | 3 }
  | { type: 'deepflow' }
  | { type: 'trance' }
  | { type: 'mythic' }
  | { type: 'auroranight' }
  | { type: 'iris' }
  | { type: 'fireflies' }
  | { type: 'twoquills'; email: string }
  | { type: 'companion'; name: string }
  | { type: 'timeback' }
  | { type: 'cleanpage' }
  | { type: 'goalset'; words: number }
  | { type: 'baptism'; title: string }
  | { type: 'booknamed'; title: string }
  | { type: 'phoenix' }
  | { type: 'prune' }
  | { type: 'earlybird' }
  | { type: 'twotales' }
  | { type: 'vigils'; n: 2 | 3 }
  | { type: 'archivemilestone'; count: number }
  | { type: 'thousand' }
  | { type: 'wish' };

const reduced = () =>
  typeof matchMedia !== 'undefined' && matchMedia('(prefers-reduced-motion: reduce)').matches;

function safeGet(key: string): string | null {
  try { return localStorage.getItem(key); } catch { return null; }
}
function safeSet(key: string, v: string) {
  try { localStorage.setItem(key, v); } catch { /* ignore */ }
}
const todayKey = () => new Date().toISOString().slice(0, 10);

/** Casca comum das cerimônias em tela cheia (backdrop + clique pra pular). */
export function Scene({ z = 140, children }: { onDone: () => void; z?: number; children: React.ReactNode }) {
  // A cena de cerimônia BLOQUEIA qualquer clique enquanto acontece (não dá pra
  // dispensar no toque nem clicar através pra UI de baixo) — ela termina sozinha
  // via useAutoDone. Como aparece no mount/entrada da tela, o usuário "toma a
  // intro" na hora, sem janela pra clicar em nada antes.
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0, transition: { duration: 0.5 } }}
      className="fixed inset-0 grid place-items-center bg-[rgba(4,7,14,0.72)] backdrop-blur-sm cursor-default"
      style={{ zIndex: z }}
      onClick={(e) => e.stopPropagation()}
      aria-hidden
    >
      {children}
    </motion.div>
  );
}

export function useAutoDone(onDone: () => void, ms: number) {
  useEffect(() => {
    const id = setTimeout(onDone, reduced() ? Math.min(ms, 2200) : ms);
    return () => clearTimeout(id);
  }, [onDone, ms]);
}

/** Uma vela (as cenas compõem várias). A chama nasce com mola, depois tremula. */
export function BornCandle({ delay = 0.55, lit = true }: { delay?: number; lit?: boolean }) {
  return (
    <span className={lit ? 'candle-mini' : 'candle-mini out'} aria-hidden>
      <motion.span
        initial={{ scale: 0, opacity: 0 }}
        animate={lit ? { scale: 1, opacity: 1 } : { scale: 0, opacity: 0 }}
        transition={{ delay, type: 'spring', stiffness: 220, damping: 11 }}
        style={{ position: 'absolute', inset: 0, transformOrigin: '50% 58%' }}
      >
        <span className="fl" style={{ position: 'absolute' }} />
      </motion.span>
      <span className="wk" />
      <span className="wx" />
    </span>
  );
}

export function SceneText({ delay, eyebrow, title, sub, warm }: { delay: number; eyebrow: string; title: string; sub?: string; warm?: boolean }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay, duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
      className="text-center"
    >
      <p className="font-mono text-[10px] uppercase tracking-[0.34em]" style={{ color: warm ? 'var(--ember-hot)' : 'var(--ember)' }}>
        ✦ {eyebrow}
      </p>
      <p className="mt-2 font-serif text-[24px] font-medium text-[#EBEEFA]">{title}</p>
      {sub && (
        <p className="mt-1 font-mono text-[9px] uppercase tracking-[0.2em] text-[#8B9AC8]">{sub}</p>
      )}
    </motion.div>
  );
}

/* ───────────────────────── a vela acende (meta do dia) ───────────────────────── */
function CandleMoment({ onDone }: { onDone: () => void }) {
  const { t } = useTranslation();
  useAutoDone(onDone, 3600);
  return (
    <Scene onDone={onDone}>
      <div className="flex flex-col items-center gap-6 px-6">
        <motion.div
          initial={{ y: 26, opacity: 0, scale: 2.8 }}
          animate={{ y: 0, opacity: 1, scale: 2.8 }}
          transition={{ type: 'spring', stiffness: 130, damping: 15 }}
          style={{ transformOrigin: 'center bottom' }}
          className="relative"
        >
          <BornCandle />
          <motion.span
            aria-hidden
            initial={{ opacity: 0 }}
            animate={{ opacity: [0, 0.9, 0.65] }}
            transition={{ delay: 0.6, duration: 1.4, times: [0, 0.4, 1] }}
            className="pointer-events-none absolute -inset-8 rounded-full"
            style={{ background: 'radial-gradient(circle at 50% 22%, rgba(246,233,200,0.4), transparent 62%)' }}
          />
        </motion.div>
        <SceneText
          delay={0.85}
          warm
          eyebrow={t('hud.candleMomentEyebrow', 'ritual cumprido')}
          title={t('hud.candleMomentTitle', 'A vela de hoje está acesa.')}
          sub={t('hud.candleMomentSub', '{{goal}} palavras escritas', { goal: DAILY_GOAL })}
        />
      </div>
    </Scene>
  );
}

/* ───────────────────────── level up ───────────────────────── */
const SPARKS = [
  { x: -70, y: -46, d: 0.0 }, { x: 64, y: -58, d: 0.12 }, { x: -38, y: -84, d: 0.22 },
  { x: 92, y: -20, d: 0.3 }, { x: -96, y: -8, d: 0.38 }, { x: 30, y: -92, d: 0.46 },
];

function LevelUpMoment({ level, onDone }: { level: number; onDone: () => void }) {
  const { t } = useTranslation();
  const C = 2 * Math.PI * 44;
  useAutoDone(onDone, 4200);
  return (
    <Scene onDone={onDone}>
      <div className="relative flex flex-col items-center gap-5 px-6">
        <motion.div
          initial={{ scale: 0.82, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ type: 'spring', stiffness: 150, damping: 14 }}
          className="relative h-[120px] w-[120px]"
        >
          <svg viewBox="0 0 100 100" className="h-full w-full -rotate-90">
            <defs>
              <linearGradient id="gradMoment" x1="0" y1="0" x2="1" y2="1">
                <stop offset="0" stopColor="#DFC993" />
                <stop offset="1" stopColor="#F6E9C8" />
              </linearGradient>
            </defs>
            <circle cx="50" cy="50" r="44" fill="none" stroke="rgba(168,184,232,0.12)" strokeWidth="5" />
            <motion.circle
              cx="50" cy="50" r="44" fill="none"
              stroke="url(#gradMoment)" strokeWidth="5" strokeLinecap="round"
              strokeDasharray={C}
              initial={{ strokeDashoffset: C }}
              animate={{ strokeDashoffset: 0 }}
              transition={{ delay: 0.25, duration: 1.1, ease: [0.22, 1, 0.36, 1] }}
            />
          </svg>
          <motion.span
            initial={{ scale: 0.4, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ delay: 1.1, type: 'spring', stiffness: 260, damping: 13 }}
            className="absolute inset-0 grid place-items-center font-serif text-[40px] font-semibold"
            style={{ color: 'var(--ember-hot)' }}
          >
            {level}
          </motion.span>
          {SPARKS.map((sp, i) => (
            <motion.span
              key={i}
              aria-hidden
              initial={{ x: 0, y: 0, opacity: 0, scale: 0.4 }}
              animate={{ x: sp.x, y: sp.y, opacity: [0, 1, 0], scale: [0.4, 1, 0.6] }}
              transition={{ delay: 1.15 + sp.d, duration: 1.15, ease: 'easeOut' }}
              className="pointer-events-none absolute left-1/2 top-1/2 text-[12px]"
              style={{ color: 'var(--ember-hot)' }}
            >
              ✦
            </motion.span>
          ))}
        </motion.div>
        <SceneText
          delay={1.3}
          eyebrow={t('hud.levelUpEyebrow', 'novo nível')}
          title={`${t('hud.lvlAbbrev', 'Nv')} ${level} · ${writerTitleOf(level, t)}`}
        />
      </div>
    </Scene>
  );
}

/* ───────────────────────── o amanhecer (100%) ───────────────────────── */
function DawnMoment({ title, onDone }: { title: string; onDone: () => void }) {
  const { t } = useTranslation();
  useAutoDone(onDone, 6000);
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0, transition: { duration: 0.9 } }}
      className="fixed inset-0 z-[145] overflow-hidden cursor-pointer"
      onClick={onDone}
    >
      <motion.div
        initial={{ y: '55%' }}
        animate={{ y: 0 }}
        transition={{ duration: reduced() ? 0 : 2.2, ease: [0.22, 1, 0.36, 1] }}
        className="absolute inset-0"
        style={{
          background:
            'linear-gradient(180deg,#070B16 0%,#131B36 30%,#33234E 56%,#7A3B4E 78%,#C4744E 94%,#E8A15D 100%)',
        }}
      />
      <motion.div
        aria-hidden
        initial={{ y: 180, opacity: 0 }}
        animate={{ y: 40, opacity: 1 }}
        transition={{ delay: 0.7, duration: 2.4, ease: [0.22, 1, 0.36, 1] }}
        className="absolute bottom-[-90px] left-1/2 h-[260px] w-[260px] -translate-x-1/2 rounded-full"
        style={{
          background: 'radial-gradient(circle,#FFE1AE,#F6E9C8 55%,rgba(223,201,147,0) 75%)',
          filter: 'blur(6px)',
        }}
      />
      <motion.span
        aria-hidden
        initial={{ opacity: 0 }}
        animate={{ opacity: 0.55, x: [0, 26, 0], y: [0, -14, 0] }}
        transition={{ delay: 1.4, duration: 5, ease: 'easeInOut' }}
        className="absolute left-[16%] top-[22%] text-[15px] text-[rgba(232,229,246,0.6)]"
      >
        ⌄⌄
      </motion.span>
      <div className="relative z-10 flex h-full flex-col items-center justify-center gap-3 px-6 text-center">
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.6, duration: 0.8 }}
          className="font-mono text-[10px] uppercase tracking-[0.34em] text-[#FFE1AE]"
        >
          ✦ {t('hud.dawnEyebrow', 'o dia raiou')}
        </motion.p>
        <motion.p
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.9, duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
          className="max-w-[26ch] font-serif text-[clamp(26px,4vw,40px)] font-medium leading-tight text-white"
        >
          {t('hud.dawnTitle', 'Manuscrito completo.')}
        </motion.p>
        {title && (
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 2.3, duration: 0.7 }}
            className="font-serif italic text-[15px] text-[rgba(244,236,255,0.85)]"
          >
            “{title}”
          </motion.p>
        )}
      </div>
    </motion.div>
  );
}

/* ─────────────── a brasinha: primeira palavra da noite (micro) ─────────────── */
function SparkMoment({ onDone }: { onDone: () => void }) {
  const { t } = useTranslation();
  useAutoDone(onDone, 3400);
  return (
    <motion.div
      initial={{ opacity: 0, y: 18 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: 10, transition: { duration: 0.4 } }}
      transition={{ type: 'spring', stiffness: 210, damping: 20 }}
      className="pointer-events-none fixed inset-x-0 bottom-[86px] z-[135] flex justify-center md:bottom-16"
    >
      <div className="flex items-center gap-3 rounded-full bg-[rgba(13,20,38,0.94)] px-4 py-2.5 shadow-[0_14px_40px_rgba(0,0,0,0.55),0_0_0_1px_rgba(168,184,232,0.14)_inset]">
        <span className="relative inline-block" style={{ transform: 'scale(0.9)' }}>
          <BornCandle delay={0.25} />
          <motion.span
            aria-hidden
            initial={{ opacity: 0 }}
            animate={{ opacity: [0, 1, 0] }}
            transition={{ duration: 0.5, times: [0, 0.5, 1] }}
            className="absolute left-1/2 top-[12px] h-[3px] w-[3px] -translate-x-1/2 rounded-full"
            style={{ background: '#F6E9C8', boxShadow: '0 0 8px 2px rgba(246,233,200,0.9)' }}
          />
        </span>
        <span className="font-mono text-[9px] uppercase tracking-[0.22em] text-[#DCE3F7]">
          {t('hud.sparkTitle', 'a primeira palavra da noite')} ✦
        </span>
      </div>
    </motion.div>
  );
}

/* ─────────────── noite lendária (1.000 palavras num dia) ─────────────── */
const NIGHT_STARS = Array.from({ length: 26 }, (_, i) => ({
  left: ((i * 37) % 100),
  top: ((i * 53) % 62) + 4,
  size: 1.5 + ((i * 7) % 3),
  d: (i % 9) * 0.14,
}));

function LegendaryMoment({ onDone }: { onDone: () => void }) {
  const { t } = useTranslation();
  useAutoDone(onDone, 4400);
  return (
    <Scene onDone={onDone} z={142}>
      <div className="absolute inset-0 overflow-hidden" aria-hidden>
        {NIGHT_STARS.map((st, i) => (
          <motion.span
            key={i}
            initial={{ opacity: 0, scale: 0 }}
            animate={{ opacity: [0, 1, 0.55], scale: 1 }}
            transition={{ delay: 0.2 + st.d, duration: 0.9 }}
            className="absolute rounded-full"
            style={{
              left: `${st.left}%`, top: `${st.top}%`,
              width: st.size, height: st.size,
              background: '#F6E9C8', boxShadow: '0 0 8px 1px rgba(246,233,200,0.7)',
            }}
          />
        ))}
      </div>
      <div className="relative flex flex-col items-center gap-4 px-6">
        <motion.span
          initial={{ scale: 0, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ delay: 0.5, type: 'spring', stiffness: 200, damping: 12 }}
          className="text-[46px] leading-none"
          style={{ color: 'var(--ember-hot)', filter: 'drop-shadow(0 0 18px rgba(246,233,200,0.8))' }}
          aria-hidden
        >
          ✦
        </motion.span>
        <SceneText
          delay={0.85}
          warm
          eyebrow={t('hud.legendaryEyebrow', 'noite lendária')}
          title={t('hud.legendaryTitle', 'Mil palavras numa noite.')}
          sub={t('hud.legendarySub', 'o céu inteiro viu')}
        />
      </div>
    </Scene>
  );
}

/* ─────────────── candelabro: marcos de ofensiva (7 · 30 · 100) ─────────────── */
function StreakMoment({ nights, onDone }: { nights: number; onDone: () => void }) {
  const { t } = useTranslation();
  useAutoDone(onDone, 4600);
  const lit = nights >= 100 ? 3 : nights >= 30 ? 2 : 1;
  return (
    <Scene onDone={onDone}>
      <div className="flex flex-col items-center gap-7 px-6">
        <motion.div
          initial={{ y: 24, opacity: 0, scale: 2.2 }}
          animate={{ y: 0, opacity: 1, scale: 2.2 }}
          transition={{ type: 'spring', stiffness: 140, damping: 15 }}
          className="flex items-end gap-6"
          style={{ transformOrigin: 'center bottom' }}
        >
          {[0, 1, 2].map((i) => (
            <BornCandle key={i} delay={0.5 + i * 0.35} lit={i < lit} />
          ))}
        </motion.div>
        <SceneText
          delay={0.6 + lit * 0.35}
          warm
          eyebrow={t('hud.streakEyebrow', 'ofensiva')}
          title={t('hud.streakTitle', '{{n}} noites seguidas.', { n: nights })}
          sub={t('hud.streakSub', 'a chama não apagou')}
        />
      </div>
    </Scene>
  );
}

/* ─────────────── vigília: 25 minutos de escrita contínua ─────────────── */
function VigilMoment({ onDone }: { onDone: () => void }) {
  const { t } = useTranslation();
  useAutoDone(onDone, 4000);
  return (
    <Scene onDone={onDone}>
      <div className="flex flex-col items-center gap-6 px-6">
        <motion.div
          initial={{ scale: 0.7, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ type: 'spring', stiffness: 150, damping: 14 }}
          className="relative h-[88px] w-[88px] overflow-hidden rounded-full"
          style={{
            background: 'radial-gradient(circle at 38% 34%,#F6E9C8,#D8C48F 62%,#8A784E 96%)',
            boxShadow: '0 0 40px rgba(246,233,200,0.4)',
          }}
        >
          <motion.span
            aria-hidden
            initial={{ x: '18%' }}
            animate={{ x: '64%' }}
            transition={{ delay: 0.5, duration: 1.6, ease: [0.22, 1, 0.36, 1] }}
            className="absolute inset-[-2%] rounded-full"
            style={{ background: '#0A101F' }}
          />
        </motion.div>
        <SceneText
          delay={1.4}
          eyebrow={t('hud.vigilEyebrow', 'vigília cumprida')}
          title={t('hud.vigilTitle', '{{min}} minutos sem soltar a pena.', { min: VIGIL_MINUTES })}
        />
      </div>
    </Scene>
  );
}

/* ─────────────── o livro nasce ─────────────── */
function BornMoment({ title, onDone }: { title: string; onDone: () => void }) {
  const { t } = useTranslation();
  useAutoDone(onDone, 4600);
  return (
    <Scene onDone={onDone}>
      <div className="flex flex-col items-center gap-7 px-6">
        <div className="relative h-[150px] w-[104px]">
          {[{ x: -34, r: -10 }, { x: 30, r: 8 }, { x: 0, r: 0 }].map((pg, i) => (
            <motion.span
              key={i}
              aria-hidden
              initial={{ x: pg.x * 3, y: -14 * i, rotate: pg.r * 2, opacity: 0 }}
              animate={{ x: 0, y: 0, rotate: 0, opacity: 1 }}
              transition={{ delay: 0.15 + i * 0.16, type: 'spring', stiffness: 130, damping: 15 }}
              className="absolute inset-0 rounded-[4px]"
              style={{ background: 'linear-gradient(180deg,#F6F1E4,#EAE2CE)', boxShadow: '0 6px 18px rgba(0,0,0,0.4)' }}
            />
          ))}
          <motion.span
            initial={{ rotateY: -100, opacity: 0 }}
            animate={{ rotateY: 0, opacity: 1 }}
            transition={{ delay: 0.85, type: 'spring', stiffness: 120, damping: 15 }}
            className="mcover flex items-end p-2.5"
            style={{ position: 'absolute', inset: 0, transformOrigin: 'left center', transformPerspective: 700, width: '100%', height: '100%' }}
          >
            <span className="mo" />
            <span className="font-serif text-[13px] font-medium leading-tight text-[#F2E9D4]" style={{ textShadow: '0 2px 8px rgba(0,0,0,0.5)' }}>
              {title}
            </span>
          </motion.span>
        </div>
        <SceneText
          delay={1.5}
          warm
          eyebrow={t('hud.bornEyebrow', 'uma nova jornada')}
          title={t('hud.bornTitle', 'O livro nasceu.')}
          sub={title}
        />
      </div>
    </Scene>
  );
}

/* ─────────────── marcos de travessia: a lua cresce (25 · 50 · 75) ─────────────── */
function CrossingMoment({ pct, title, onDone }: { pct: number; title: string; onDone: () => void }) {
  const { t } = useTranslation();
  useAutoDone(onDone, 4200);
  const shadowX = pct >= 75 ? '74%' : pct >= 50 ? '50%' : '28%';
  return (
    <Scene onDone={onDone}>
      <div className="flex flex-col items-center gap-6 px-6">
        <motion.div
          initial={{ y: 46, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ type: 'spring', stiffness: 120, damping: 15 }}
          className="relative h-[96px] w-[96px] overflow-hidden rounded-full"
          style={{
            background: 'radial-gradient(circle at 38% 34%,#F6E9C8,#D8C48F 62%,#8A784E 96%)',
            boxShadow: '0 0 44px rgba(246,233,200,0.42)',
          }}
        >
          <motion.span
            aria-hidden
            initial={{ x: '4%' }}
            animate={{ x: shadowX }}
            transition={{ delay: 0.5, duration: 1.5, ease: [0.22, 1, 0.36, 1] }}
            className="absolute inset-[-2%] rounded-full"
            style={{ background: '#0A101F' }}
          />
        </motion.div>
        <SceneText
          delay={1.3}
          eyebrow={t('hud.crossingEyebrow', 'a travessia')}
          title={t('hud.crossingTitle', '{{pct}}% do manuscrito.', { pct })}
          sub={title}
        />
      </div>
    </Scene>
  );
}

/* ─────────────── o lacre: exportação pronta ─────────────── */
function SealMoment({ title, onDone }: { title: string; onDone: () => void }) {
  const { t } = useTranslation();
  useAutoDone(onDone, 3800);
  return (
    <Scene onDone={onDone}>
      <div className="flex flex-col items-center gap-6 px-6">
        <motion.div
          initial={{ y: 30, opacity: 0, rotate: -2 }}
          animate={{ y: 0, opacity: 1, rotate: -1 }}
          transition={{ type: 'spring', stiffness: 140, damping: 15 }}
          className="relative w-[240px] rounded-[3px_8px_8px_3px] px-6 py-7 text-center"
          style={{
            background: 'linear-gradient(180deg,#F6F1E4,#EAE2CE)',
            boxShadow: '0 30px 70px -18px rgba(0,0,0,0.8), 0 0 0 1px rgba(70,55,30,0.25)',
          }}
        >
          <p className="font-mono text-[7.5px] uppercase tracking-[0.26em] text-[#A8935F]">
            {t('hud.sealEyebrow', 'selado e pronto')}
          </p>
          <p className="mt-2 font-serif text-[17px] font-medium leading-tight text-[#241C36]">{title}</p>
          <motion.span
            initial={{ scale: 2.2, opacity: 0, rotate: -30 }}
            animate={{ scale: 1, opacity: 1, rotate: -8 }}
            transition={{ delay: 0.7, type: 'spring', stiffness: 320, damping: 14 }}
            className="absolute -bottom-6 left-1/2 grid h-[52px] w-[52px] -translate-x-1/2 place-items-center rounded-full text-[18px]"
            style={{
              background: 'radial-gradient(circle at 36% 30%, var(--ember-hot), var(--ember) 55%, #9A8756)',
              color: '#141005',
              boxShadow: '0 8px 22px rgba(0,0,0,0.5), 0 0 24px rgba(223,201,147,0.45)',
            }}
            aria-hidden
          >
            ✦
          </motion.span>
        </motion.div>
        <SceneText
          delay={1.15}
          warm
          eyebrow={t('hud.sealSub', 'exportação concluída')}
          title={t('hud.sealTitle', 'Seu livro partiu para o mundo.')}
        />
      </div>
    </Scene>
  );
}

/* ─────────────── a ficha arquivada (nova entrada no Universo) ─────────────── */
function FiledMoment({ name, label, onDone }: { name: string; label: string; onDone: () => void }) {
  const { t } = useTranslation();
  useAutoDone(onDone, 3600);
  return (
    <Scene onDone={onDone}>
      <motion.div
        initial={{ y: 40, rotate: 3, opacity: 0 }}
        animate={{ y: 0, rotate: -1, opacity: 1 }}
        exit={{ y: 60, scale: 0.9, opacity: 0, transition: { duration: 0.3 } }}
        transition={{ type: 'spring', stiffness: 150, damping: 16 }}
        className="relative w-[min(340px,88vw)] rounded-[3px_8px_8px_3px] px-6 py-6"
        style={{
          background: 'linear-gradient(180deg,#F6F1E4,#EAE2CE)',
          boxShadow: '0 40px 90px -20px rgba(0,0,0,0.8), 0 0 0 1px rgba(70,55,30,0.25)',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <p className="font-mono text-[7.5px] uppercase tracking-[0.28em] text-[#A8935F]">
          {t('storyBible.dossier', 'Arquivo do Universo')} · {label}
        </p>
        <div className="mt-2 overflow-hidden">
          <motion.p
            initial={{ width: '0%' }}
            animate={{ width: '100%' }}
            transition={{ delay: 0.35, duration: 0.8, ease: 'linear' }}
            className="whitespace-nowrap overflow-hidden font-serif text-[22px] font-medium text-[#241C36]"
          >
            {name}
          </motion.p>
        </div>
        {[82, 64, 71].map((w, i) => (
          <motion.span
            key={i}
            aria-hidden
            initial={{ width: 0, opacity: 0 }}
            animate={{ width: `${w}%`, opacity: 1 }}
            transition={{ delay: 1.05 + i * 0.18, duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
            className="mt-2.5 block h-[5px] rounded-full"
            style={{ background: 'rgba(70,55,30,0.16)' }}
          />
        ))}
        <motion.span
          initial={{ scale: 2.6, opacity: 0, rotate: 4 }}
          animate={{ scale: 1, opacity: 1, rotate: -12 }}
          transition={{ delay: 1.85, type: 'spring', stiffness: 380, damping: 15 }}
          className="absolute right-4 top-9 rounded-[4px] border-[2.5px] px-2.5 py-1 font-mono text-[11px] font-bold uppercase tracking-[0.2em]"
          style={{ color: '#8A3B2E', borderColor: 'rgba(138,59,46,0.75)', background: 'rgba(246,241,228,0.6)' }}
          aria-hidden
        >
          {t('hud.filedStamp', 'Arquivado')}
        </motion.span>
        {/* legenda: o que acabou de acontecer */}
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 2.2, duration: 0.5 }}
          className="absolute -bottom-10 left-0 right-0 text-center font-mono text-[9px] uppercase tracking-[0.26em]"
          style={{ color: 'var(--ember-hot)' }}
        >
          ✦ {t('hud.filedSub', 'nova entrada guardada no Universo')}
        </motion.p>
      </motion.div>
    </Scene>
  );
}

/* ─────────── a ficha é removida do arquivo ─────────── */
function BurnMoment({ name, onDone }: { name: string; onDone: () => void }) {
  const { t } = useTranslation();
  useAutoDone(onDone, 3000);
  return (
    <Scene onDone={onDone}>
      <motion.div
        initial={{ y: 34, rotate: 2, opacity: 0 }}
        animate={{ y: 0, rotate: -1, opacity: 1 }}
        exit={{ y: 120, rotate: 8, opacity: 0, transition: { duration: 0.45, ease: [0.5, 0, 0.9, 0.4] } }}
        transition={{ type: 'spring', stiffness: 160, damping: 16 }}
        className="relative w-[min(300px,86vw)] rounded-[3px_8px_8px_3px] px-6 py-6"
        style={{
          background: 'linear-gradient(180deg,#F6F1E4,#EAE2CE)',
          boxShadow: '0 40px 90px -20px rgba(0,0,0,0.8), 0 0 0 1px rgba(70,55,30,0.25)',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <p className="font-mono text-[7.5px] uppercase tracking-[0.28em] text-[#A8935F]">
          {t('storyBible.dossier', 'Arquivo do Universo')}
        </p>
        {/* o nome ganha o risco de caneta */}
        <div className="relative mt-2 inline-block">
          <p className="font-serif text-[22px] font-medium text-[#241C36]">{name}</p>
          <motion.span
            aria-hidden
            initial={{ width: 0 }}
            animate={{ width: '104%' }}
            transition={{ delay: 0.7, duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
            className="absolute left-[-2%] top-1/2 h-[2.5px] -translate-y-1/2 rounded-full"
            style={{ background: 'rgba(138,59,46,0.85)' }}
          />
        </div>
        <motion.span
          initial={{ scale: 2.6, opacity: 0, rotate: 6 }}
          animate={{ scale: 1, opacity: 1, rotate: -10 }}
          transition={{ delay: 1.05, type: 'spring', stiffness: 380, damping: 15 }}
          className="absolute right-4 bottom-5 rounded-[4px] border-[2.5px] px-2.5 py-1 font-mono text-[11px] font-bold uppercase tracking-[0.2em]"
          style={{ color: '#8A3B2E', borderColor: 'rgba(138,59,46,0.75)', background: 'rgba(246,241,228,0.6)' }}
          aria-hidden
        >
          {t('hud.burnStamp', 'Removido')}
        </motion.span>
      </motion.div>
    </Scene>
  );
}

/* ─────────── o livro é encerrado (exclusão) ─────────── */
function UnbindMoment({ title, onDone }: { title: string; onDone: () => void }) {
  const { t } = useTranslation();
  useAutoDone(onDone, 3800);
  return (
    <Scene onDone={onDone}>
      <div className="flex flex-col items-center gap-7 px-6">
        <div className="relative h-[140px] w-[98px]">
          {/* as páginas se soltam e caem */}
          {[{ x: -46, r: -16, d: 0.55 }, { x: 40, r: 12, d: 0.7 }, { x: -14, r: -7, d: 0.85 }].map((pg, i) => (
            <motion.span
              key={i}
              aria-hidden
              initial={{ x: 0, y: 0, rotate: 0, opacity: 1 }}
              animate={{ x: pg.x, y: 150, rotate: pg.r * 2, opacity: 0 }}
              transition={{ delay: pg.d, duration: 1.3, ease: [0.4, 0, 0.8, 0.5] }}
              className="absolute inset-0 rounded-[4px]"
              style={{ background: 'linear-gradient(180deg,#F6F1E4,#EAE2CE)', boxShadow: '0 6px 18px rgba(0,0,0,0.4)' }}
            />
          ))}
          {/* a capa fica e apaga por último */}
          <motion.span
            initial={{ opacity: 1 }}
            animate={{ opacity: 0.25, filter: 'grayscale(0.9)' }}
            transition={{ delay: 1.5, duration: 1 }}
            className="mcover flex items-end p-2.5"
            style={{ position: 'absolute', inset: 0, width: '100%', height: '100%' }}
          >
            <span className="mo" />
            <span className="font-serif text-[12px] font-medium leading-tight text-[#F2E9D4]">{title}</span>
          </motion.span>
        </div>
        <SceneText
          delay={1.1}
          eyebrow={t('hud.unbindEyebrow', 'arquivo morto')}
          title={t('hud.unbindTitle', 'A jornada foi encerrada.')}
          sub={title}
        />
      </div>
    </Scene>
  );
}

/* ─────────── a cópia se desdobra (duplicar) ─────────── */
function TwinMoment({ title, onDone }: { title: string; onDone: () => void }) {
  const { t } = useTranslation();
  useAutoDone(onDone, 3200);
  return (
    <Scene onDone={onDone}>
      <div className="flex flex-col items-center gap-7 px-6">
        <div className="relative h-[132px] w-[190px] overflow-visible">
          {/* a cópia desliza pra direita, o original acomoda à esquerda — tudo dentro da caixa */}
          <motion.span
            initial={{ x: 0, rotate: 0 }}
            animate={{ x: 30, rotate: 4 }}
            transition={{ delay: 0.55, type: 'spring', stiffness: 170, damping: 15 }}
            className="mcover flex items-end p-2"
            style={{ position: 'absolute', left: '50%', top: 0, height: '100%', width: 92, marginLeft: -46, filter: 'brightness(0.8)' }}
            aria-hidden
          >
            <span className="mo" />
          </motion.span>
          <motion.span
            initial={{ x: 0 }}
            animate={{ x: -30, rotate: -4 }}
            transition={{ delay: 0.55, type: 'spring', stiffness: 170, damping: 15 }}
            className="mcover flex items-end p-2"
            style={{ position: 'absolute', left: '50%', top: 0, height: '100%', width: 92, marginLeft: -46 }}
          >
            <span className="mo" />
            <span className="font-serif text-[11px] font-medium leading-tight text-[#F2E9D4]">{title}</span>
          </motion.span>
          <motion.span
            aria-hidden
            initial={{ opacity: 0, scale: 0 }}
            animate={{ opacity: [0, 1, 0], scale: [0.5, 1.2, 0.8] }}
            transition={{ delay: 0.85, duration: 0.9 }}
            className="absolute right-2 top-0 text-[16px]"
            style={{ color: 'var(--ember-hot)' }}
          >
            ✦
          </motion.span>
        </div>
        <SceneText
          delay={1.1}
          warm
          eyebrow={t('hud.twinEyebrow', 'duplicado')}
          title={t('hud.twinTitle', 'Uma cópia foi criada.')}
          sub={title}
        />
      </div>
    </Scene>
  );
}

/* ─────────── a capa nova (flip com a arte real) ─────────── */
function CoverSwapMoment({ title, img, onDone }: { title: string; img?: string; onDone: () => void }) {
  const { t } = useTranslation();
  useAutoDone(onDone, 3400);
  return (
    <Scene onDone={onDone}>
      <div className="flex flex-col items-center gap-7 px-6">
        <motion.div
          initial={{ rotateY: 92, opacity: 0 }}
          animate={{ rotateY: 0, opacity: 1 }}
          transition={{ delay: 0.2, type: 'spring', stiffness: 140, damping: 16 }}
          style={{ transformPerspective: 900, transformOrigin: 'left center' }}
          className="relative h-[168px] w-[114px] overflow-hidden rounded-[6px_10px_10px_6px] shadow-[0_30px_60px_-16px_rgba(0,0,0,0.8)]"
        >
          {img ? (
            <img src={img} alt={title} className="h-full w-full object-cover" />
          ) : (
            <span className="mcover flex items-end p-2.5" style={{ position: 'absolute', inset: 0, width: '100%', height: '100%' }}>
              <span className="mo" />
              <span className="font-serif text-[12px] font-medium leading-tight text-[#F2E9D4]">{title}</span>
            </span>
          )}
          {/* brilho varre a capa nova */}
          <motion.span
            aria-hidden
            initial={{ x: '-130%' }}
            animate={{ x: '130%' }}
            transition={{ delay: 0.9, duration: 0.8, ease: 'easeOut' }}
            className="absolute inset-0"
            style={{ background: 'linear-gradient(115deg,transparent 40%,rgba(255,255,255,0.3) 52%,transparent 64%)' }}
          />
        </motion.div>
        <SceneText
          delay={1.05}
          warm
          eyebrow={t('hud.coverEyebrow', 'capa nova')}
          title={t('hud.coverTitle', 'O rosto do livro mudou.')}
          sub={title}
        />
      </div>
    </Scene>
  );
}

/* ─────────── a pena voa (mensagem enviada · link copiado) ─────────── */
function FeatherMoment({ onDone }: { onDone: () => void }) {
  useAutoDone(onDone, 1500);
  return (
    <motion.div
      initial={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="pointer-events-none fixed inset-0 z-[135]"
      aria-hidden
    >
      <motion.span
        initial={{ x: '38vw', y: '78vh', rotate: 12, opacity: 0 }}
        animate={{ x: '68vw', y: '30vh', rotate: -18, opacity: [0, 1, 1, 0] }}
        transition={{ duration: 1.35, ease: [0.3, 0.6, 0.4, 1] }}
        className="absolute text-[22px]"
        style={{ color: 'var(--ember-hot)', filter: 'drop-shadow(0 0 10px rgba(246,233,200,0.7))' }}
      >
        🪶
      </motion.span>
      <motion.span
        initial={{ x: '40vw', y: '80vh', opacity: 0 }}
        animate={{ x: '62vw', y: '40vh', opacity: [0, 0.9, 0] }}
        transition={{ duration: 1.2, delay: 0.15, ease: 'easeOut' }}
        className="absolute text-[10px]"
        style={{ color: 'var(--ember-hot)' }}
      >
        ✦
      </motion.span>
    </motion.div>
  );
}

/* ─────────── semana completa: sete noites, sete estrelas ─────────── */
function FullWeekMoment({ onDone }: { onDone: () => void }) {
  const { t } = useTranslation();
  useAutoDone(onDone, 4600);
  const stars = [0, 1, 2, 3, 4, 5, 6];
  return (
    <Scene onDone={onDone}>
      <div className="flex flex-col items-center gap-8 px-6">
        <div className="relative flex items-end gap-7">
          {stars.map((i) => {
            const lift = [8, 22, 34, 40, 34, 22, 8][i];
            return (
              <motion.span
                key={i}
                initial={{ scale: 0, opacity: 0, y: 0 }}
                animate={{ scale: 1, opacity: 1, y: -lift }}
                transition={{ delay: 0.3 + i * 0.16, type: 'spring', stiffness: 300, damping: 12 }}
                className="relative text-[15px]"
                style={{
                  color: 'var(--ember-hot)',
                  filter: 'drop-shadow(0 0 10px rgba(246,233,200,0.85))',
                }}
                aria-hidden
              >
                ✦
              </motion.span>
            );
          })}
        </div>
        <SceneText
          delay={1.6}
          warm
          eyebrow={t('hud.fullweekEyebrow', 'semana completa')}
          title={t('hud.fullweekTitle', 'Sete noites, sete estrelas.')}
          sub={t('hud.fullweekSub', 'nenhuma noite ficou no escuro')}
        />
      </div>
    </Scene>
  );
}

/* ─────────── o tinteiro secou (cota de IA do ciclo) ─────────── */
function InkDryMoment({ onDone }: { onDone: () => void }) {
  const { t } = useTranslation();
  useAutoDone(onDone, 4200);
  return (
    <Scene onDone={onDone}>
      <div className="flex flex-col items-center gap-7 px-6">
        <motion.div
          initial={{ y: 20, opacity: 0, rotate: 0 }}
          animate={{ y: 0, opacity: 1, rotate: -14 }}
          transition={{ type: 'spring', stiffness: 140, damping: 13 }}
          className="relative h-[74px] w-[62px]"
        >
          {/* o tinteiro de vidro, inclinado e vazio */}
          <span className="absolute bottom-0 left-1/2 h-[52px] w-[52px] -translate-x-1/2 rounded-[10px_10px_16px_16px]"
            style={{ background: 'linear-gradient(160deg, rgba(168,184,232,0.28), rgba(20,31,61,0.9) 55%)', boxShadow: '0 0 0 1.5px rgba(168,184,232,0.35) inset, 0 14px 30px rgba(0,0,0,0.5)' }} />
          <span className="absolute top-1 left-1/2 h-[14px] w-[22px] -translate-x-1/2 rounded-[3px]"
            style={{ background: 'rgba(168,184,232,0.3)', boxShadow: '0 0 0 1px rgba(168,184,232,0.35) inset' }} />
          {/* o restinho de tinta no fundo */}
          <span className="absolute bottom-[5px] left-1/2 h-[9px] w-[36px] -translate-x-1/2 rounded-full" style={{ background: '#0A101F' }} />
          {/* a última gota cai... e apaga */}
          <motion.span
            aria-hidden
            initial={{ y: 0, opacity: 0, scale: 1 }}
            animate={{ y: 64, opacity: [0, 1, 1, 0], scale: [1, 1, 0.6, 0.2] }}
            transition={{ delay: 0.8, duration: 1.1, ease: [0.5, 0, 0.9, 0.5] }}
            className="absolute left-[68%] top-[46px] h-[8px] w-[6px] rounded-[50%_50%_60%_60%]"
            style={{ background: '#0A101F', boxShadow: '0 0 6px rgba(10,16,31,0.8)' }}
          />
        </motion.div>
        <SceneText
          delay={1.5}
          eyebrow={t('hud.inkdryEyebrow', 'o tinteiro secou')}
          title={t('hud.inkdryTitle', 'A tinta desta lua acabou.')}
          sub={t('hud.inkdrySub', 'ela volta no próximo ciclo — ou com um plano maior')}
        />
      </div>
    </Scene>
  );
}

/* ─────────── a carta chega (mensagem nova) — micro ─────────── */
function LetterMoment({ onDone }: { onDone: () => void }) {
  const { t } = useTranslation();
  useAutoDone(onDone, 3200);
  return (
    <motion.div
      initial={{ x: 80, y: 12, opacity: 0, rotate: 5 }}
      animate={{ x: 0, y: 0, opacity: 1, rotate: -2 }}
      exit={{ x: 60, opacity: 0, transition: { duration: 0.35 } }}
      transition={{ type: 'spring', stiffness: 220, damping: 20 }}
      className="pointer-events-none fixed bottom-[86px] right-5 z-[135] md:bottom-8"
      aria-hidden
    >
      <div className="relative w-[168px] rounded-[4px] px-4 pb-3 pt-4 text-center"
        style={{ background: 'linear-gradient(180deg,#F6F1E4,#EAE2CE)', boxShadow: '0 20px 46px rgba(0,0,0,0.6), 0 0 0 1px rgba(70,55,30,0.25)' }}>
        {/* aba do envelope */}
        <span className="absolute -top-px left-0 right-0 h-[26px]"
          style={{ clipPath: 'polygon(0 0, 50% 100%, 100% 0)', background: 'linear-gradient(180deg,#EDE4CE,#E2D7BC)' }} />
        {/* o lacre pulsa uma vez */}
        <motion.span
          initial={{ scale: 1.6, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ delay: 0.4, type: 'spring', stiffness: 340, damping: 13 }}
          className="relative z-10 mx-auto grid h-[26px] w-[26px] place-items-center rounded-full text-[10px]"
          style={{ background: 'radial-gradient(circle at 36% 30%, var(--ember-hot), var(--ember) 60%, #9A8756)', color: '#141005', boxShadow: '0 4px 12px rgba(0,0,0,0.4)' }}
        >
          ✦
        </motion.span>
        <p className="relative z-10 mt-2 font-mono text-[7.5px] uppercase tracking-[0.2em] text-[#A8935F]">
          {t('hud.letterTitle', 'uma carta chegou')}
        </p>
      </div>
    </motion.div>
  );
}

/* ─────────── dez almas no universo ─────────── */
const RING10 = Array.from({ length: 10 }, (_, i) => {
  const a = (i / 10) * Math.PI * 2 - Math.PI / 2;
  return { x: Math.cos(a) * 62, y: Math.sin(a) * 62, d: i * 0.13 };
});

function TenSoulsMoment({ onDone }: { onDone: () => void }) {
  const { t } = useTranslation();
  useAutoDone(onDone, 4800);
  return (
    <Scene onDone={onDone}>
      <div className="flex flex-col items-center gap-8 px-6">
        <div className="relative h-[150px] w-[150px]">
          {RING10.map((pt, i) => (
            <motion.span
              key={i}
              aria-hidden
              initial={{ x: 0, y: 0, opacity: 0, scale: 0 }}
              animate={{ x: pt.x, y: pt.y, opacity: 1, scale: 1 }}
              transition={{ delay: 0.3 + pt.d, type: 'spring', stiffness: 200, damping: 14 }}
              className="absolute left-1/2 top-1/2 -ml-[5px] -mt-[5px] text-[11px]"
              style={{ color: 'var(--ember-hot)', filter: 'drop-shadow(0 0 8px rgba(246,233,200,0.8))' }}
            >
              ✦
            </motion.span>
          ))}
          <motion.span
            initial={{ scale: 0, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ delay: 1.7, type: 'spring', stiffness: 240, damping: 13 }}
            className="absolute inset-0 grid place-items-center font-serif text-[42px] font-semibold"
            style={{ color: 'var(--ember-hot)' }}
          >
            10
          </motion.span>
        </div>
        <SceneText
          delay={1.9}
          warm
          eyebrow={t('hud.tensoulsEyebrow', 'o universo cresce')}
          title={t('hud.tensoulsTitle', 'Dez almas habitam seu mundo.')}
        />
      </div>
    </Scene>
  );
}

/* ─────────── meia-noite: a lua no zênite ─────────── */
function MidnightMoment({ onDone }: { onDone: () => void }) {
  const { t } = useTranslation();
  useAutoDone(onDone, 4400);
  return (
    <Scene onDone={onDone}>
      <div className="flex flex-col items-center gap-7 px-6">
        <motion.div
          initial={{ y: 90, opacity: 0, scale: 0.8 }}
          animate={{ y: 0, opacity: 1, scale: 1 }}
          transition={{ duration: 1.6, ease: [0.22, 1, 0.36, 1] }}
          className="h-[92px] w-[92px] rounded-full"
          style={{
            background: 'radial-gradient(circle at 38% 34%,#F6E9C8,#D8C48F 62%,#8A784E 96%)',
            boxShadow: '0 0 70px rgba(246,233,200,0.5), 0 0 130px rgba(246,233,200,0.2)',
          }}
        />
        <SceneText
          delay={1.4}
          eyebrow={t('hud.midnightEyebrow', 'meia-noite')}
          title={t('hud.midnightTitle', 'O dia virou — e a história continua.')}
        />
      </div>
    </Scene>
  );
}

/* ─────────── salva no último instante (1ª palavra depois das 23h) ─────────── */
function LastCallMoment({ onDone }: { onDone: () => void }) {
  const { t } = useTranslation();
  useAutoDone(onDone, 4200);
  return (
    <Scene onDone={onDone}>
      <div className="flex flex-col items-center gap-6 px-6">
        <motion.div
          initial={{ y: 26, opacity: 0, scale: 2.6 }}
          animate={{ y: 0, opacity: 1, scale: 2.6 }}
          transition={{ type: 'spring', stiffness: 130, damping: 15 }}
          style={{ transformOrigin: 'center bottom' }}
          className="relative"
        >
          <span className="candle-mini" aria-hidden>
            {/* a chama nasce TREMENDO e se firma */}
            <motion.span
              initial={{ scale: 0, opacity: 0, rotate: 0 }}
              animate={{ scale: [0, 1.15, 0.85, 1.08, 1], opacity: 1, rotate: [0, -14, 12, -7, 0] }}
              transition={{ delay: 0.5, duration: 1.5, times: [0, 0.3, 0.5, 0.75, 1] }}
              style={{ position: 'absolute', inset: 0, transformOrigin: '50% 58%' }}
            >
              <span className="fl" style={{ position: 'absolute' }} />
            </motion.span>
            <span className="wk" />
            <span className="wx" />
          </span>
        </motion.div>
        <SceneText
          delay={1.6}
          warm
          eyebrow={t('hud.lastcallEyebrow', 'no último instante')}
          title={t('hud.lastcallTitle', 'A chama quase apagou — você chegou a tempo.')}
        />
      </div>
    </Scene>
  );
}

/* ─────────── o fitilho marca a página (saiu do editor) — micro ─────────── */
function BookmarkMoment({ onDone }: { onDone: () => void }) {
  const { t } = useTranslation();
  useAutoDone(onDone, 3000);
  return (
    <motion.div
      initial={{ opacity: 0, y: 14 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: 8, transition: { duration: 0.35 } }}
      transition={{ type: 'spring', stiffness: 220, damping: 20 }}
      className="pointer-events-none fixed inset-x-0 bottom-[86px] z-[135] flex justify-center md:bottom-10"
      aria-hidden
    >
      <div className="relative flex items-center gap-3 overflow-visible rounded-full bg-[rgba(13,20,38,0.94)] px-5 py-2.5 shadow-[0_14px_40px_rgba(0,0,0,0.55),0_0_0_1px_rgba(168,184,232,0.14)_inset]">
        {/* o fitilho desce por cima do pill */}
        <motion.span
          initial={{ scaleY: 0 }}
          animate={{ scaleY: 1 }}
          transition={{ delay: 0.25, duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
          className="h-[30px] w-[9px] origin-top -my-3"
          style={{
            background: 'linear-gradient(180deg,var(--ember-hot),var(--ember))',
            clipPath: 'polygon(0 0,100% 0,100% 100%,50% 82%,0 100%)',
            filter: 'drop-shadow(0 3px 6px rgba(0,0,0,0.4))',
          }}
        />
        <span className="font-mono text-[9px] uppercase tracking-[0.22em] text-[#DCE3F7]">
          {t('hud.bookmarkTitle', 'página marcada — até a próxima noite')}
        </span>
      </div>
    </motion.div>
  );
}

/* ─────────── a lamparina: um mês de jornada ─────────── */
function LanternMoment({ title, onDone }: { title: string; onDone: () => void }) {
  const { t } = useTranslation();
  useAutoDone(onDone, 4400);
  return (
    <Scene onDone={onDone}>
      <div className="flex flex-col items-center gap-8 px-6">
        <motion.div
          initial={{ y: 22, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ type: 'spring', stiffness: 140, damping: 14 }}
          className="relative h-[150px] w-[96px]"
        >
          {/* alça */}
          <span className="absolute left-1/2 top-0 h-[24px] w-[36px] -translate-x-1/2 rounded-t-full"
            style={{ border: '4px solid #6B7BA8', borderBottom: 'none' }} />
          {/* tampa */}
          <span className="absolute left-1/2 top-[20px] h-[10px] w-[64px] -translate-x-1/2 rounded-[4px]"
            style={{ background: 'linear-gradient(180deg,#8B9AC8,#4E5B85)' }} />
          {/* corpo: vidro quente emoldurado */}
          <span className="absolute left-1/2 top-[30px] h-[86px] w-[72px] -translate-x-1/2 rounded-[8px]"
            style={{
              background: 'radial-gradient(circle at 50% 62%, rgba(246,233,200,0.55), rgba(223,201,147,0.18) 55%, rgba(13,20,38,0.6) 90%)',
              boxShadow: '0 0 0 4px #4E5B85 inset, 0 0 44px rgba(246,233,200,0.45)',
            }} />
          {/* travessas da moldura */}
          <span className="absolute left-1/2 top-[30px] h-[86px] w-[4px] -translate-x-1/2" style={{ background: '#4E5B85', marginLeft: -20 }} />
          <span className="absolute left-1/2 top-[30px] h-[86px] w-[4px] -translate-x-1/2" style={{ background: '#4E5B85', marginLeft: 20 }} />
          {/* base */}
          <span className="absolute bottom-0 left-1/2 h-[14px] w-[64px] -translate-x-1/2 rounded-[4px]"
            style={{ background: 'linear-gradient(180deg,#8B9AC8,#4E5B85)' }} />
          {/* a chama, GRANDE, dentro do vidro */}
          <motion.span
            initial={{ scale: 0, opacity: 0 }}
            animate={{ scale: 1.7, opacity: 1 }}
            transition={{ delay: 0.55, type: 'spring', stiffness: 200, damping: 11 }}
            className="absolute left-1/2 top-[62px] -translate-x-1/2"
            style={{ transformOrigin: 'center bottom' }}
            aria-hidden
          >
            <span className="flame-loose" />
          </motion.span>
          {/* halo que respira */}
          <motion.span
            aria-hidden
            initial={{ opacity: 0 }}
            animate={{ opacity: [0, 0.9, 0.65] }}
            transition={{ delay: 0.7, duration: 1.6, times: [0, 0.4, 1] }}
            className="pointer-events-none absolute -inset-10 rounded-full"
            style={{ background: 'radial-gradient(circle, rgba(246,233,200,0.4), transparent 60%)' }}
          />
        </motion.div>
        <SceneText
          delay={1.2}
          warm
          eyebrow={t('hud.lanternEyebrow', 'um mês de jornada')}
          title={t('hud.lanternTitle', 'Trinta noites iluminando esta história.')}
          sub={title}
        />
      </div>
    </Scene>
  );
}

/* ─────────── o floreio: mil palavras num capítulo — micro ─────────── */
function FlourishMoment({ onDone }: { onDone: () => void }) {
  const { t } = useTranslation();
  useAutoDone(onDone, 3200);
  return (
    <motion.div
      initial={{ opacity: 0, y: 14 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: 8, transition: { duration: 0.35 } }}
      transition={{ type: 'spring', stiffness: 220, damping: 20 }}
      className="pointer-events-none fixed inset-x-0 bottom-[86px] z-[135] flex justify-center md:bottom-10"
      aria-hidden
    >
      <div className="rounded-full bg-[rgba(13,20,38,0.94)] px-5 py-3 shadow-[0_14px_40px_rgba(0,0,0,0.55),0_0_0_1px_rgba(168,184,232,0.14)_inset]">
        <p className="font-mono text-[9px] uppercase tracking-[0.22em] text-[#DCE3F7]">
          ✒️ {t('hud.flourishTitle', 'mil palavras neste capítulo')}
        </p>
        {/* a linha de tinta se desenha */}
        <motion.span
          initial={{ width: 0 }}
          animate={{ width: '100%' }}
          transition={{ delay: 0.35, duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
          className="mt-1.5 block h-[2px] rounded-full"
          style={{ background: 'linear-gradient(90deg, var(--ember), var(--ember-hot))', boxShadow: '0 0 8px rgba(223,201,147,0.6)' }}
        />
      </div>
    </motion.div>
  );
}

/* ─────────── a teia da trama: os três atos de pé ─────────── */
function WeaveMoment({ onDone }: { onDone: () => void }) {
  const { t } = useTranslation();
  useAutoDone(onDone, 4400);
  const acts = [
    { x: 30, label: 'I', color: '#5B8DEF' },
    { x: 130, label: 'II', color: '#A78BFA' },
    { x: 230, label: 'III', color: 'var(--ember)' },
  ];
  return (
    <Scene onDone={onDone}>
      <div className="flex flex-col items-center gap-7 px-6">
        <div className="relative h-[80px] w-[260px]">
          <svg viewBox="0 0 260 80" className="absolute inset-0 h-full w-full">
            <motion.path
              d="M30 40 C 70 10, 90 70, 130 40 S 190 10, 230 40"
              fill="none"
              stroke="rgba(223,201,147,0.5)"
              strokeWidth="1.5"
              initial={{ pathLength: 0 }}
              animate={{ pathLength: 1 }}
              transition={{ delay: 0.9, duration: 1.2, ease: [0.22, 1, 0.36, 1] }}
            />
          </svg>
          {acts.map((a, i) => (
            <motion.span
              key={a.label}
              initial={{ scale: 0, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ delay: 0.3 + i * 0.25, type: 'spring', stiffness: 240, damping: 13 }}
              className="absolute top-1/2 grid h-[40px] w-[40px] -translate-y-1/2 place-items-center rounded-full font-serif text-[15px] font-semibold"
              style={{
                left: a.x - 20,
                color: '#EBEEFA',
                background: 'rgba(13,20,38,0.9)',
                boxShadow: `0 0 0 1.5px ${a.color}, 0 0 18px -2px ${a.color}`,
              }}
            >
              {a.label}
            </motion.span>
          ))}
        </div>
        <SceneText
          delay={1.8}
          warm
          eyebrow={t('hud.weaveEyebrow', 'a teia da trama')}
          title={t('hud.weaveTitle', 'Os três atos estão de pé.')}
        />
      </div>
    </Scene>
  );
}

/* ─────────── lua cheia de verdade (calendário lunar) ─────────── */
function FullMoonMoment({ onDone }: { onDone: () => void }) {
  const { t } = useTranslation();
  useAutoDone(onDone, 4600);
  return (
    <Scene onDone={onDone}>
      <div className="flex flex-col items-center gap-8 px-6">
        <motion.div
          initial={{ y: 70, opacity: 0, scale: 0.7 }}
          animate={{ y: 0, opacity: 1, scale: 1 }}
          transition={{ duration: 2, ease: [0.22, 1, 0.36, 1] }}
          className="relative h-[128px] w-[128px] rounded-full"
          style={{
            background: 'radial-gradient(circle at 38% 34%,#F6E9C8,#D8C48F 62%,#8A784E 96%)',
            boxShadow: '0 0 80px rgba(246,233,200,0.55), 0 0 160px rgba(246,233,200,0.25)',
          }}
        >
          {/* crateras sutis */}
          <span className="absolute left-[26%] top-[30%] h-[14px] w-[14px] rounded-full" style={{ background: 'rgba(138,120,78,0.35)' }} />
          <span className="absolute left-[58%] top-[52%] h-[9px] w-[9px] rounded-full" style={{ background: 'rgba(138,120,78,0.3)' }} />
          <span className="absolute left-[40%] top-[66%] h-[6px] w-[6px] rounded-full" style={{ background: 'rgba(138,120,78,0.28)' }} />
        </motion.div>
        <SceneText
          delay={1.6}
          eyebrow={t('hud.fullmoonEyebrow', 'lua cheia')}
          title={t('hud.fullmoonTitle', 'A noite pede história.')}
        />
      </div>
    </Scene>
  );
}

/* ─────────── o pacto: primeira vez no editor ─────────── */
function OathMoment({ onDone }: { onDone: () => void }) {
  const { t } = useTranslation();
  useAutoDone(onDone, 4800);
  return (
    <Scene onDone={onDone}>
      <div className="flex flex-col items-center gap-7 px-6">
        <motion.div
          initial={{ y: 26, opacity: 0, rotate: -1.5 }}
          animate={{ y: 0, opacity: 1, rotate: -1 }}
          transition={{ type: 'spring', stiffness: 150, damping: 15 }}
          className="relative w-[min(320px,86vw)] rounded-[3px_8px_8px_3px] px-7 py-7"
          style={{
            background: 'linear-gradient(180deg,#F6F1E4,#EAE2CE)',
            boxShadow: '0 40px 90px -20px rgba(0,0,0,0.8), 0 0 0 1px rgba(70,55,30,0.25)',
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <p className="text-center font-serif italic text-[15px] leading-relaxed text-[#4E4430]">
            “{t('hud.oathQuote', 'Que esta seja a primeira de muitas noites.')}”
          </p>
          {/* a assinatura se desenha, com a pena correndo junto */}
          <div className="relative mx-auto mt-6 h-[24px] w-[170px]">
            <motion.span
              initial={{ width: 0 }}
              animate={{ width: '100%' }}
              transition={{ delay: 0.9, duration: 1.3, ease: [0.4, 0, 0.4, 1] }}
              className="absolute bottom-1 left-0 block h-[2px] rounded-full"
              style={{ background: 'linear-gradient(90deg, transparent, #2A2438 20%, #2A2438)' }}
            />
            <motion.span
              aria-hidden
              initial={{ left: 0, opacity: 0, rotate: 24 }}
              animate={{ left: '92%', opacity: [0, 1, 1, 0], rotate: 24 }}
              transition={{ delay: 0.9, duration: 1.3, ease: [0.4, 0, 0.4, 1] }}
              className="absolute -top-2 text-[16px]"
              style={{ color: '#2A2438' }}
            >
              🪶
            </motion.span>
          </div>
        </motion.div>
        <SceneText
          delay={2.3}
          warm
          eyebrow={t('hud.oathEyebrow', 'o pacto')}
          title={t('hud.oathTitle', 'A jornada começa esta noite.')}
        />
      </div>
    </Scene>
  );
}

/* ─────────── estante cheia: o 5º mundo ─────────── */
function ShelfFullMoment({ count, onDone }: { count: number; onDone: () => void }) {
  const { t } = useTranslation();
  useAutoDone(onDone, 4400);
  const spines = [
    { h: 64, c: 'linear-gradient(180deg,#2E3A63,#1A2340)' },
    { h: 78, c: 'linear-gradient(180deg,#1E4B4B,#122F2F)' },
    { h: 70, c: 'linear-gradient(180deg,#5A2E44,#3A1D2C)' },
    { h: 82, c: 'linear-gradient(180deg,#8A6A2E,#5E451A)' },
    { h: 68, c: 'linear-gradient(180deg,#39325E,#241F42)' },
  ];
  return (
    <Scene onDone={onDone}>
      <div className="flex flex-col items-center gap-7 px-6">
        <div>
          <div className="flex items-end justify-center gap-[6px]">
            {spines.map((sp, i) => (
              <motion.span
                key={i}
                initial={{ y: -26, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: 0.3 + i * 0.17, type: 'spring', stiffness: 260, damping: 17 }}
                className="w-[17px] rounded-[3px_3px_1px_1px]"
                style={{ height: sp.h, background: sp.c, boxShadow: '0 0 0 1px rgba(255,255,255,0.06) inset' }}
                aria-hidden
              />
            ))}
          </div>
          <motion.span
            initial={{ scaleX: 0 }}
            animate={{ scaleX: 1 }}
            transition={{ delay: 0.2, duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
            className="mt-0 block h-[7px] w-[150px] rounded-[3px]"
            style={{ background: 'linear-gradient(180deg,#233052,#141F3D)', boxShadow: '0 8px 20px -6px rgba(0,0,0,0.8)' }}
            aria-hidden
          />
        </div>
        <SceneText
          delay={1.4}
          warm
          eyebrow={t('hud.shelffullEyebrow', 'a estante cresce')}
          title={t('hud.shelffullTitle', '{{n}} mundos lado a lado.', { n: count })}
        />
      </div>
    </Scene>
  );
}

/* ─────────── tempestade de ideias: o roteiro nasceu ─────────── */
const IDEA_RAIN = Array.from({ length: 14 }, (_, i) => ({
  left: 12 + ((i * 61) % 76),
  d: (i % 7) * 0.16,
  size: 9 + ((i * 3) % 5),
}));

function StormMoment({ onDone }: { onDone: () => void }) {
  const { t } = useTranslation();
  useAutoDone(onDone, 4400);
  return (
    <Scene onDone={onDone}>
      <div className="relative flex h-[220px] w-[min(340px,88vw)] flex-col items-center justify-end overflow-hidden px-6">
        {IDEA_RAIN.map((st, i) => (
          <motion.span
            key={i}
            aria-hidden
            initial={{ y: -30, opacity: 0 }}
            animate={{ y: 150, opacity: [0, 1, 1, 0] }}
            transition={{ delay: 0.25 + st.d, duration: 1.4, ease: [0.4, 0, 0.7, 0.4] }}
            className="absolute top-0"
            style={{ left: `${st.left}%`, fontSize: st.size, color: 'var(--ember-hot)', filter: 'drop-shadow(0 0 6px rgba(246,233,200,0.7))' }}
          >
            ✦
          </motion.span>
        ))}
        <SceneText
          delay={1.6}
          warm
          eyebrow={t('hud.stormEyebrow', 'tempestade de ideias')}
          title={t('hud.stormTitle', 'O roteiro está pronto.')}
        />
      </div>
    </Scene>
  );
}

/* ─────────── o retorno: a história esperou ─────────── */
const DUST = Array.from({ length: 8 }, (_, i) => ({ x: 18 + ((i * 37) % 64), d: i * 0.12 }));

function ReturnMoment({ onDone }: { onDone: () => void }) {
  const { t } = useTranslation();
  useAutoDone(onDone, 4600);
  return (
    <Scene onDone={onDone}>
      <div className="flex flex-col items-center gap-8 px-6">
        <div className="relative h-[110px] w-[150px]">
          {/* livro aberto simplificado */}
          {[0, 1].map((side) => (
            <motion.span
              key={side}
              initial={{ rotateY: side === 0 ? 0 : 0, opacity: 0, y: 14 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.25, type: 'spring', stiffness: 150, damping: 15 }}
              className="absolute bottom-0 h-[84px] w-[72px]"
              style={{
                left: side === 0 ? 2 : 76,
                background: 'linear-gradient(180deg,#F6F1E4,#EAE2CE)',
                borderRadius: side === 0 ? '8px 2px 2px 8px' : '2px 8px 8px 2px',
                boxShadow: side === 0
                  ? 'inset -10px 0 16px -12px rgba(70,55,30,0.5), 0 18px 36px rgba(0,0,0,0.5)'
                  : 'inset 10px 0 16px -12px rgba(70,55,30,0.5), 0 18px 36px rgba(0,0,0,0.5)',
              }}
              aria-hidden
            />
          ))}
          {/* a poeira sobe quando o livro respira de novo */}
          {DUST.map((dm, i) => (
            <motion.span
              key={i}
              aria-hidden
              initial={{ y: 0, opacity: 0 }}
              animate={{ y: -56, opacity: [0, 0.85, 0] }}
              transition={{ delay: 0.7 + dm.d, duration: 1.7, ease: 'easeOut' }}
              className="absolute bottom-[70px] h-[3px] w-[3px] rounded-full"
              style={{ left: `${dm.x}%`, background: '#D8C48F', boxShadow: '0 0 6px rgba(216,196,143,0.8)' }}
            />
          ))}
        </div>
        <SceneText
          delay={1.5}
          warm
          eyebrow={t('hud.returnEyebrow', 'bem-vindo de volta')}
          title={t('hud.returnTitle', 'A história esperou por você.')}
        />
      </div>
    </Scene>
  );
}

/* ─────────── a página arrancada (capítulo excluído) ─────────── */
function TearPageMoment({ onDone }: { onDone: () => void }) {
  const { t } = useTranslation();
  useAutoDone(onDone, 3400);
  return (
    <Scene onDone={onDone}>
      <div className="flex flex-col items-center gap-7 px-6">
        <motion.span
          initial={{ y: -30, rotate: -4, opacity: 0 }}
          animate={{ y: [null, 10, 90], rotate: [-4, 6, -8], opacity: [0, 1, 0] }}
          transition={{ duration: 2, ease: 'easeInOut', times: [0, 0.4, 1] }}
          className="block h-[110px] w-[80px] rounded-[3px]"
          style={{
            background: 'linear-gradient(180deg,#F6F1E4,#EAE2CE)',
            boxShadow: '0 14px 30px rgba(0,0,0,0.5)',
            clipPath: 'polygon(0 2%, 8% 0, 22% 2.5%, 38% 0.5%, 55% 2%, 72% 0, 88% 2%, 100% 1%, 100% 100%, 0 100%)',
          }}
          aria-hidden
        />
        <SceneText
          delay={1.1}
          eyebrow={t('hud.tearpageEyebrow', 'corte do editor')}
          title={t('hud.tearpageTitle', 'Uma página saiu da história.')}
        />
      </div>
    </Scene>
  );
}

/* ─────────── a primeira palavra de um livro ─────────── */
function FirstWordMoment({ title, onDone }: { title: string; onDone: () => void }) {
  const { t } = useTranslation();
  useAutoDone(onDone, 4200);
  return (
    <Scene onDone={onDone}>
      <div className="flex flex-col items-center gap-7 px-6">
        <div className="relative grid h-[110px] w-[110px] place-items-center">
          {/* ondas do nascimento */}
          {[0, 1].map((r) => (
            <motion.span
              key={r}
              aria-hidden
              initial={{ scale: 0.3, opacity: 0 }}
              animate={{ scale: 1.5 + r * 0.5, opacity: [0, 0.6, 0] }}
              transition={{ delay: 0.6 + r * 0.35, duration: 1.5, ease: 'easeOut' }}
              className="absolute inset-0 rounded-full"
              style={{ border: '1.5px solid rgba(246,233,200,0.6)' }}
            />
          ))}
          <motion.span
            initial={{ scale: 0, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ delay: 0.45, type: 'spring', stiffness: 220, damping: 11 }}
            className="text-[40px]"
            style={{ color: 'var(--ember-hot)', filter: 'drop-shadow(0 0 20px rgba(246,233,200,0.85))' }}
            aria-hidden
          >
            ✦
          </motion.span>
        </div>
        <SceneText
          delay={1.3}
          warm
          eyebrow={t('hud.firstwordEyebrow', 'a primeira palavra')}
          title={t('hud.firstwordTitle', 'Toda história começa assim.')}
          sub={title}
        />
      </div>
    </Scene>
  );
}

/* ─────────── o pergaminho: aliado convocado ─────────── */
function ScrollMoment({ name, onDone }: { name: string; onDone: () => void }) {
  const { t } = useTranslation();
  useAutoDone(onDone, 4200);
  return (
    <Scene onDone={onDone}>
      <div className="flex flex-col items-center gap-6 px-6">
        <div className="w-[min(300px,86vw)]">
          {/* bastão de cima */}
          <span className="block h-[10px] rounded-full" style={{ background: 'linear-gradient(180deg,#8B9AC8,#4E5B85)' }} />
          {/* o pergaminho desenrola */}
          <motion.div
            initial={{ scaleY: 0 }}
            animate={{ scaleY: 1 }}
            transition={{ delay: 0.35, duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
            className="origin-top px-6 py-6 text-center"
            style={{
              background: 'linear-gradient(180deg,#F6F1E4,#EAE2CE)',
              boxShadow: '0 30px 60px -16px rgba(0,0,0,0.7), 0 0 0 1px rgba(70,55,30,0.25)',
            }}
          >
            <p className="font-mono text-[7.5px] uppercase tracking-[0.26em] text-[#A8935F]">
              {t('hud.scrollEyebrow', 'aliado convocado')}
            </p>
            <p className="mt-2 font-serif text-[19px] font-medium text-[#241C36]">{name}</p>
            <motion.span
              initial={{ width: 0 }}
              animate={{ width: '60%' }}
              transition={{ delay: 1.3, duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
              className="mx-auto mt-3 block h-[2px] rounded-full"
              style={{ background: 'rgba(70,55,30,0.4)' }}
            />
          </motion.div>
          <span className="block h-[10px] rounded-full" style={{ background: 'linear-gradient(180deg,#8B9AC8,#4E5B85)' }} />
        </div>
        <SceneText
          delay={1.7}
          warm
          eyebrow={t('hud.scrollSub', 'parceria')}
          title={t('hud.scrollTitle', 'A jornada ganhou companhia.')}
        />
      </div>
    </Scene>
  );
}

/* ─────────── lua nova: só as suas estrelas ─────────── */
const NM_STARS = Array.from({ length: 7 }, (_, i) => ({
  x: [-92, -58, 66, 96, -20, 34, 8][i], y: [-38, 44, -52, 20, -78, 58, -14][i], d: i * 0.16,
}));

function NewMoonMoment({ onDone }: { onDone: () => void }) {
  const { t } = useTranslation();
  useAutoDone(onDone, 4400);
  return (
    <Scene onDone={onDone}>
      <div className="flex flex-col items-center gap-8 px-6">
        <div className="relative grid h-[120px] w-[120px] place-items-center">
          {/* o disco escuro da lua nova, só o aro respira */}
          <motion.span
            initial={{ scale: 0.7, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ duration: 1.4, ease: [0.22, 1, 0.36, 1] }}
            className="h-[96px] w-[96px] rounded-full"
            style={{ background: '#0A101F', boxShadow: '0 0 0 1.5px rgba(216,196,143,0.35), 0 0 34px rgba(216,196,143,0.12)' }}
            aria-hidden
          />
          {NM_STARS.map((st, i) => (
            <motion.span
              key={i}
              aria-hidden
              initial={{ opacity: 0, scale: 0 }}
              animate={{ opacity: [0, 1, 0.7], scale: 1 }}
              transition={{ delay: 0.8 + st.d, duration: 0.8 }}
              className="absolute left-1/2 top-1/2 text-[10px]"
              style={{ marginLeft: st.x, marginTop: st.y, color: 'var(--ember-hot)', filter: 'drop-shadow(0 0 8px rgba(246,233,200,0.8))' }}
            >
              ✦
            </motion.span>
          ))}
        </div>
        <SceneText
          delay={1.7}
          warm
          eyebrow={t('hud.newmoonEyebrow', 'lua nova')}
          title={t('hud.newmoonTitle', 'Só as suas estrelas brilham hoje.')}
        />
      </div>
    </Scene>
  );
}

/* ─────────── cem mil palavras: a pilha de manuscritos ─────────── */
function HundredKMoment({ onDone }: { onDone: () => void }) {
  const { t } = useTranslation();
  useAutoDone(onDone, 4600);
  const stack = [
    { w: 118, r: -2 }, { w: 112, r: 3 }, { w: 120, r: -1 }, { w: 110, r: 2 }, { w: 116, r: -3 },
  ];
  return (
    <Scene onDone={onDone}>
      <div className="flex flex-col items-center gap-7 px-6">
        <div className="relative flex h-[130px] w-[150px] flex-col-reverse items-center justify-start">
          {stack.map((pg, i) => (
            <motion.span
              key={i}
              aria-hidden
              initial={{ y: -60, opacity: 0, rotate: 0 }}
              animate={{ y: 0, opacity: 1, rotate: pg.r }}
              transition={{ delay: 0.3 + i * 0.22, type: 'spring', stiffness: 260, damping: 18 }}
              className="block h-[20px] rounded-[3px]"
              style={{
                width: pg.w,
                marginTop: -4,
                background: 'linear-gradient(180deg,#F6F1E4,#E4D9BF)',
                boxShadow: '0 5px 12px rgba(0,0,0,0.45), 0 0 0 1px rgba(70,55,30,0.2)',
              }}
            />
          ))}
          <motion.span
            initial={{ scale: 0, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ delay: 1.6, type: 'spring', stiffness: 240, damping: 13 }}
            className="absolute -top-3 font-serif text-[26px] font-semibold"
            style={{ color: 'var(--ember-hot)', textShadow: '0 0 18px rgba(246,233,200,0.6)' }}
          >
            100.000
          </motion.span>
        </div>
        <SceneText
          delay={1.9}
          warm
          eyebrow={t('hud.hundredkEyebrow', 'marco do escritor')}
          title={t('hud.hundredkTitle', 'Cem mil palavras suas no mundo.')}
        />
      </div>
    </Scene>
  );
}

/* ─────────── a espinha engrossa: dez capítulos ─────────── */
function Spine10Moment({ title, onDone }: { title: string; onDone: () => void }) {
  const { t } = useTranslation();
  useAutoDone(onDone, 4600);
  return (
    <Scene onDone={onDone}>
      <div className="flex flex-col items-center gap-7 px-6">
        <div className="relative h-[170px] w-[52px] rounded-[5px] p-[7px]"
          style={{ background: 'linear-gradient(168deg,#1B2A52,#0E1730 55%,#0A1024)', boxShadow: '0 0 0 1px rgba(168,184,232,0.25) inset, 0 24px 48px rgba(0,0,0,0.6)' }}>
          <div className="flex h-full flex-col justify-between">
            {Array.from({ length: 10 }, (_, i) => (
              <motion.span
                key={i}
                aria-hidden
                initial={{ scaleX: 0, opacity: 0 }}
                animate={{ scaleX: 1, opacity: 1 }}
                transition={{ delay: 0.3 + i * 0.13, duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
                className="block h-[6px] origin-left rounded-full"
                style={{ background: i === 9 ? 'var(--ember-hot)' : 'rgba(168,184,232,0.4)', boxShadow: i === 9 ? '0 0 10px rgba(246,233,200,0.7)' : undefined }}
              />
            ))}
          </div>
        </div>
        <SceneText
          delay={1.7}
          warm
          eyebrow={t('hud.spine10Eyebrow', 'a espinha engrossa')}
          title={t('hud.spine10Title', 'Dez capítulos de história.')}
          sub={title}
        />
      </div>
    </Scene>
  );
}

/* ─────────── recorde pessoal: a melhor noite ─────────── */
function RecordMoment({ words, onDone }: { words: number; onDone: () => void }) {
  const { t } = useTranslation();
  useAutoDone(onDone, 4400);
  return (
    <Scene onDone={onDone}>
      <div className="flex flex-col items-center gap-7 px-6">
        <div className="relative h-[110px] w-[220px]">
          {/* marca antiga */}
          <span className="absolute bottom-0 left-[30px] h-[58px] w-[44px] rounded-t-[5px]"
            style={{ background: 'rgba(107,123,168,0.35)' }} aria-hidden />
          <span className="absolute bottom-[62px] left-[18px] font-mono text-[7px] uppercase tracking-[0.16em] text-[#6B7BA8]">
            {t('hud.recordOld', 'antiga')}
          </span>
          {/* a barra nova sobe e passa */}
          <motion.span
            aria-hidden
            initial={{ height: 0 }}
            animate={{ height: 96 }}
            transition={{ delay: 0.4, duration: 1.1, ease: [0.22, 1, 0.36, 1] }}
            className="absolute bottom-0 left-[122px] w-[44px] rounded-t-[5px]"
            style={{ background: 'linear-gradient(180deg,var(--ember-hot),var(--ember))', boxShadow: '0 0 24px rgba(223,201,147,0.5)' }}
          />
          <motion.span
            initial={{ scale: 0, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ delay: 1.4, type: 'spring', stiffness: 300, damping: 12 }}
            className="absolute left-[132px] top-[-6px] text-[17px]"
            style={{ color: 'var(--ember-hot)', filter: 'drop-shadow(0 0 12px rgba(246,233,200,0.9))' }}
            aria-hidden
          >
            ✦
          </motion.span>
        </div>
        <SceneText
          delay={1.6}
          warm
          eyebrow={t('hud.recordEyebrow', 'recorde pessoal')}
          title={t('hud.recordTitle', 'Sua melhor noite até agora.')}
          sub={t('hud.recordSub', '{{n}} palavras', { n: words.toLocaleString('pt-BR') })}
        />
      </div>
    </Scene>
  );
}

/* ─────────── mês constelado: 20 noites no mês ─────────── */
const MONTH_ARC = Array.from({ length: 20 }, (_, i) => {
  const a = Math.PI * (0.15 + (i / 19) * 0.7);
  return { x: Math.cos(a) * 120, y: -Math.sin(a) * 62, d: i * 0.07 };
});

function MonthStarsMoment({ nights, onDone }: { nights: number; onDone: () => void }) {
  const { t } = useTranslation();
  useAutoDone(onDone, 4800);
  return (
    <Scene onDone={onDone}>
      <div className="flex flex-col items-center gap-4 px-6">
        <div className="relative h-[86px] w-[270px]">
          {MONTH_ARC.map((st, i) => (
            <motion.span
              key={i}
              aria-hidden
              initial={{ opacity: 0, scale: 0 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.3 + st.d, type: 'spring', stiffness: 320, damping: 14 }}
              className="absolute left-1/2 top-[78px] text-[9px]"
              style={{ marginLeft: st.x - 4, marginTop: st.y, color: 'var(--ember-hot)', filter: 'drop-shadow(0 0 7px rgba(246,233,200,0.8))' }}
            >
              ✦
            </motion.span>
          ))}
        </div>
        <SceneText
          delay={1.9}
          warm
          eyebrow={t('hud.monthstarsEyebrow', 'mês constelado')}
          title={t('hud.monthstarsTitle', '{{n}} noites de escrita este mês.', { n: nights })}
        />
      </div>
    </Scene>
  );
}

/* ─────────── o cartógrafo: um universo de todos os tipos ─────────── */
function CartographerMoment({ onDone }: { onDone: () => void }) {
  const { t } = useTranslation();
  useAutoDone(onDone, 4600);
  const points = [
    { x: 0, y: -52, c: '#5B8DEF' }, { x: 52, y: 0, c: 'var(--aurora)' },
    { x: 0, y: 52, c: '#A78BFA' }, { x: -52, y: 0, c: 'var(--ember)' },
  ];
  return (
    <Scene onDone={onDone}>
      <div className="flex flex-col items-center gap-7 px-6">
        <div className="relative grid h-[140px] w-[140px] place-items-center">
          {/* rosa dos ventos */}
          <motion.span
            initial={{ scale: 0.6, opacity: 0, rotate: -40 }}
            animate={{ scale: 1, opacity: 1, rotate: 0 }}
            transition={{ type: 'spring', stiffness: 130, damping: 14 }}
            className="h-[86px] w-[86px] rounded-full"
            style={{ boxShadow: '0 0 0 1.5px rgba(168,184,232,0.35), 0 0 0 5px rgba(168,184,232,0.06)' }}
            aria-hidden
          />
          <span className="absolute font-serif text-[19px] font-semibold" style={{ color: '#EBEEFA' }}>✧</span>
          {points.map((pt, i) => (
            <motion.span
              key={i}
              aria-hidden
              initial={{ x: 0, y: 0, opacity: 0, scale: 0 }}
              animate={{ x: pt.x, y: pt.y, opacity: 1, scale: 1 }}
              transition={{ delay: 0.6 + i * 0.22, type: 'spring', stiffness: 260, damping: 13 }}
              className="absolute h-[13px] w-[13px] rounded-full"
              style={{ background: pt.c, boxShadow: `0 0 14px ${pt.c}` }}
            />
          ))}
        </div>
        <SceneText
          delay={1.7}
          warm
          eyebrow={t('hud.cartoEyebrow', 'o cartógrafo')}
          title={t('hud.cartoTitle', 'Seu mundo tem todos os pontos cardeais.')}
          sub={t('hud.cartoSub', 'personagens · lugares · lore · mais')}
        />
      </div>
    </Scene>
  );
}

/* ─────────── cinquenta velas: 50 noites de meta batida ─────────── */
const CANDLE_FIELD = Array.from({ length: 50 }, (_, i) => ({
  x: (i % 10) * 26, y: Math.floor(i / 10) * 24, d: ((i * 13) % 50) * 0.045,
}));

function FiftyCandlesMoment({ onDone }: { onDone: () => void }) {
  const { t } = useTranslation();
  useAutoDone(onDone, 5000);
  return (
    <Scene onDone={onDone}>
      <div className="flex flex-col items-center gap-8 px-6">
        <div className="relative h-[110px] w-[250px]">
          {CANDLE_FIELD.map((cf, i) => (
            <motion.span
              key={i}
              aria-hidden
              initial={{ opacity: 0, scale: 0 }}
              animate={{ opacity: [0, 1, 0.85], scale: 1 }}
              transition={{ delay: 0.3 + cf.d, duration: 0.6 }}
              className="absolute h-[7px] w-[5px] rounded-[50%_50%_45%_45%]"
              style={{
                left: cf.x, top: cf.y,
                background: 'radial-gradient(circle at 50% 78%,#FFF6DC,#F6E9C8 55%,rgba(223,201,147,0))',
                filter: 'drop-shadow(0 0 5px rgba(246,233,200,0.8))',
              }}
            />
          ))}
        </div>
        <SceneText
          delay={2.6}
          warm
          eyebrow={t('hud.fiftyEyebrow', 'o velário')}
          title={t('hud.fiftyTitle', 'Cinquenta noites com a vela acesa.')}
        />
      </div>
    </Scene>
  );
}

/* ─────────── o rosto sai da névoa: primeira foto de personagem ─────────── */
function RevealMoment({ name, img, onDone }: { name: string; img?: string; onDone: () => void }) {
  const { t } = useTranslation();
  useAutoDone(onDone, 4600);
  return (
    <Scene onDone={onDone}>
      <div className="flex flex-col items-center gap-7 px-6">
        <motion.div
          initial={{ y: 24, opacity: 0, rotate: 4 }}
          animate={{ y: 0, opacity: 1, rotate: 2 }}
          transition={{ type: 'spring', stiffness: 150, damping: 15 }}
          className="w-[150px] bg-white p-2 pb-8 shadow-[0_24px_50px_-12px_rgba(0,0,0,0.7)]"
        >
          <div className="relative aspect-square w-full overflow-hidden">
            {img ? (
              <motion.img
                src={img}
                alt={name}
                initial={{ filter: 'blur(14px) grayscale(1) brightness(0.7)', scale: 1.12 }}
                animate={{ filter: 'blur(0px) grayscale(0) brightness(1)', scale: 1 }}
                transition={{ delay: 0.6, duration: 1.6, ease: [0.22, 1, 0.36, 1] }}
                className="h-full w-full object-cover"
              />
            ) : (
              <motion.div
                initial={{ filter: 'blur(14px) brightness(0.5)' }}
                animate={{ filter: 'blur(0px) brightness(1)' }}
                transition={{ delay: 0.6, duration: 1.6 }}
                className="grid h-full w-full place-items-center font-serif text-[52px] font-semibold text-[#070B16]"
                style={{ background: 'linear-gradient(135deg,#7EA4F5,#5B8DEF)' }}
              >
                {name.charAt(0).toUpperCase()}
              </motion.div>
            )}
          </div>
          <p className="mt-2 text-center font-serif italic text-[12px] text-[#5C5138]">{name}</p>
        </motion.div>
        <SceneText
          delay={1.9}
          warm
          eyebrow={t('hud.revealEyebrow', 'revelação')}
          title={t('hud.revealTitle', 'O rosto saiu da névoa.')}
        />
      </div>
    </Scene>
  );
}

/* ─────────── à luz de vela: primeira vez no modo papel ─────────── */
function PaperLightMoment({ onDone }: { onDone: () => void }) {
  const { t } = useTranslation();
  useAutoDone(onDone, 4200);
  return (
    <Scene onDone={onDone}>
      <div className="flex flex-col items-center gap-7 px-6">
        <div className="flex items-end gap-5">
          <motion.div
            initial={{ y: 20, opacity: 0, rotate: -2 }}
            animate={{ y: 0, opacity: 1, rotate: -1 }}
            transition={{ type: 'spring', stiffness: 150, damping: 15 }}
            className="h-[110px] w-[86px] rounded-[3px] p-3"
            style={{ background: 'linear-gradient(180deg,#F7F1E0,#EDE3C9)', boxShadow: '0 20px 44px rgba(0,0,0,0.55), 0 0 0 1px rgba(70,55,30,0.2)' }}
            aria-hidden
          >
            {[70, 85, 60, 78, 40].map((w, i) => (
              <motion.span
                key={i}
                initial={{ width: 0 }}
                animate={{ width: `${w}%` }}
                transition={{ delay: 0.7 + i * 0.14, duration: 0.4 }}
                className="mb-[7px] block h-[3px] rounded-full"
                style={{ background: 'rgba(69,58,94,0.35)' }}
              />
            ))}
          </motion.div>
          <motion.span
            initial={{ opacity: 0, scale: 0.6 }}
            animate={{ opacity: 1, scale: 1.6 }}
            transition={{ delay: 0.4, type: 'spring', stiffness: 180, damping: 13 }}
            style={{ transformOrigin: 'center bottom' }}
          >
            <BornCandle delay={0.7} />
          </motion.span>
        </div>
        <SceneText
          delay={1.5}
          warm
          eyebrow={t('hud.paperlightEyebrow', 'modo papel')}
          title={t('hud.paperlightTitle', 'Escrevendo à luz de vela.')}
        />
      </div>
    </Scene>
  );
}

/* ─────────── o manuscrito chegou: livro importado ─────────── */
function ArrivedMoment({ title, onDone }: { title: string; onDone: () => void }) {
  const { t } = useTranslation();
  useAutoDone(onDone, 4600);
  return (
    <Scene onDone={onDone}>
      <div className="flex flex-col items-center gap-7 px-6">
        <div className="relative h-[130px] w-[120px]">
          {/* páginas sobem do pacote */}
          {[{ x: -26, r: -9 }, { x: 24, r: 8 }, { x: 0, r: 0 }].map((pg, i) => (
            <motion.span
              key={i}
              aria-hidden
              initial={{ y: 24, x: 0, rotate: 0, opacity: 0 }}
              animate={{ y: -34, x: pg.x, rotate: pg.r, opacity: 1 }}
              transition={{ delay: 1.0 + i * 0.16, type: 'spring', stiffness: 150, damping: 16 }}
              className="absolute bottom-[36px] left-1/2 h-[74px] w-[54px] rounded-[3px]"
              style={{ marginLeft: -27, background: 'linear-gradient(180deg,#F6F1E4,#EAE2CE)', boxShadow: '0 8px 18px rgba(0,0,0,0.45)' }}
            />
          ))}
          {/* o pacote de papel pardo */}
          <motion.span
            initial={{ y: 26, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ type: 'spring', stiffness: 170, damping: 15 }}
            className="absolute bottom-0 left-1/2 h-[52px] w-[96px] -translate-x-1/2 rounded-[4px]"
            style={{ background: 'linear-gradient(180deg,#8A6A45,#6E5236)', boxShadow: '0 14px 30px rgba(0,0,0,0.5), 0 0 0 1px rgba(255,255,255,0.06) inset' }}
            aria-hidden
          />
          {/* o cordão se desfaz */}
          <motion.span
            aria-hidden
            initial={{ opacity: 1, scaleY: 1 }}
            animate={{ opacity: 0, scaleY: 0.2 }}
            transition={{ delay: 0.75, duration: 0.4 }}
            className="absolute bottom-0 left-1/2 h-[52px] w-[3px] -translate-x-1/2"
            style={{ background: '#D8C48F' }}
          />
        </div>
        <SceneText
          delay={1.7}
          warm
          eyebrow={t('hud.arrivedEyebrow', 'entrega especial')}
          title={t('hud.arrivedTitle', 'O manuscrito chegou em segurança.')}
          sub={title}
        />
      </div>
    </Scene>
  );
}

/* ─────────── fio de brasa: a ofensiva viva na base da tela ───────────
   tier 1 (3+ noites): fio discreto · tier 2 (7+): brilho com shimmer ·
   tier 3 (30+): mais forte, com respiração. Ambiente permanente. */
export function StreakEmberLine({ streak }: { streak: number }) {
  if (streak < 3) return null;
  const tier = streak >= 30 ? 3 : streak >= 7 ? 2 : 1;
  return (
    <div
      aria-hidden
      className={`ember-line ember-line-${tier} pointer-events-none fixed inset-x-0 bottom-0 z-[60]`}
    />
  );
}

/* ─────────── céu por nível: o Início ganha estrelas conforme você sobe ─────────── */
const LEVEL_SKY = Array.from({ length: 60 }, (_, i) => ({
  x: ((i * 41) % 100), y: ((i * 59) % 92) + 2, s: 1 + (i % 2), d: (i % 12) * 0.6,
}));

export function LevelStarfield({ level }: { level: number }) {
  const count = Math.min(10 + level * 6, 60);
  return (
    <span aria-hidden className="pointer-events-none absolute inset-0 overflow-hidden">
      {LEVEL_SKY.slice(0, count).map((st, i) => (
        <span
          key={i}
          className="level-star absolute rounded-full"
          style={{
            left: `${st.x}%`, top: `${st.y}%`, width: st.s, height: st.s,
            animationDelay: `${st.d}s`,
          }}
        />
      ))}
    </span>
  );
}

/* ─────────── estrelas cadentes ambientes (Início) ─────────── */
export function ShootingStars() {
  return (
    <span aria-hidden className="pointer-events-none absolute inset-0 overflow-hidden">
      <span className="shooting-star" style={{ top: '12%', left: '8%', animationDelay: '14s' }} />
      <span className="shooting-star" style={{ top: '30%', left: '55%', animationDelay: '67s' }} />
    </span>
  );
}

/* ───────────────────── vigia dos gatilhos ───────────────────── */
export function MomentsWatcher() {
  const { t } = useTranslation();
  const { projects, getTodayStats, dailyStats } = useAppStore();
  // ── orquestrador ──────────────────────────────────────────────────────
  // As cenas entram numa FILA: uma toca por vez, com respiro de 650ms entre
  // elas; pedidos duplicados (mesmo type) são ignorados; a fila guarda no
  // máximo 4 — o resto cai (a próxima conquista real reacende o gatilho).
  const [moment, setMomentState] = useState<Moment | null>(null);
  const playingRef = useRef<Moment | null>(null);
  const queueRef = useRef<Moment[]>([]);
  const setMoment = (m: Moment) => {
    if (playingRef.current?.type === m.type) return;
    if (queueRef.current.some((q) => q.type === m.type)) return;
    if (playingRef.current) {
      if (queueRef.current.length < 4) queueRef.current.push(m);
      return;
    }
    playingRef.current = m;
    setMomentState(m);
  };

  const todayWords = getTodayStats()?.wordsWritten ?? 0;
  const level = useMemo(() => writerLevelOf(totalWordsOf(projects)).level, [projects]);

  // sessão: valores no momento em que o app abriu (gatilhos "ao vivo")
  const sessionStart = useRef<{ words: number; at: number } | null>(null);
  if (sessionStart.current === null) sessionStart.current = { words: todayWords, at: Date.now() };

  // baseline silencioso do 1º uso: o que JÁ aconteceu não vira cerimônia
  const baselined = useRef(false);
  if (!baselined.current && projects.length > 0) {
    baselined.current = true;
    if (!safeGet('moments-baseline-v1')) {
      for (const p of projects) {
        safeSet(`moment-born-${p.id}`, '1');
        const words = p.chapters.reduce((s, c) => s + c.wordCount, 0);
        for (const th of [25, 50, 75]) {
          if ((words / NOVEL_TARGET) * 100 >= th) safeSet(`moment-cross-${p.id}-${th}`, '1');
        }
        if (words >= NOVEL_TARGET) safeSet(`moment-dawn-${p.id}`, '1');
      }
      safeSet('moments-baseline-v1', '1');
    }
    if (!safeGet('moments-baseline-v2')) {
      for (const p of projects) {
        const words = p.chapters.reduce((sum, c) => sum + c.wordCount, 0);
        if (words > 0) safeSet(`moment-firstword-${p.id}`, '1');
      }
      if (projects.length >= 5) safeSet('moment-shelffull', '1');
      safeSet('moments-baseline-v2', '1');
    }
    if (!safeGet('moments-baseline-v4')) {
      for (const p of projects) {
        if ((p.metadata.title || '').trim()) safeSet(`moment-booknamed-${p.id}`, '1');
      }
      safeSet('moments-baseline-v4', '1');
    }
    if (!safeGet('moments-baseline-v3')) {
      if (totalWordsOf(projects) >= 100_000) safeSet('moment-100k', '1');
      for (const p of projects) {
        if (p.chapters.length >= 10) safeSet(`moment-spine10-${p.id}`, '1');
      }
      safeSet('moments-baseline-v3', '1');
    }
  }

  // barramento: fluxos do app (export, universo) + atalho playMoment()
  useEffect(() => {
    const onEvent = (ev: Event) => {
      const d = (ev as CustomEvent).detail as Moment | undefined;
      if (d?.type) setMoment(d);
    };
    window.addEventListener('reformu:moment', onEvent);
    const play = (type: string) => {
      const title = projects[0]?.metadata.title || 'Seu livro';
      const map: Record<string, Moment> = {
        candle: { type: 'candle' },
        level: { type: 'level', level: writerLevelOf(totalWordsOf(projects)).level + 1 },
        dawn: { type: 'dawn', title },
        spark: { type: 'spark' },
        legendary: { type: 'legendary' },
        streak: { type: 'streak', nights: 7 },
        vigil: { type: 'vigil' },
        born: { type: 'born', title },
        crossing: { type: 'crossing', pct: 50, title },
        seal: { type: 'seal', title },
        file: { type: 'file', name: 'Helena', label: 'Personagem' },
        burn: { type: 'burn', name: 'Helena' },
        unbind: { type: 'unbind', title },
        twin: { type: 'twin', title },
        coverswap: { type: 'coverswap', title },
        feather: { type: 'feather' },
        fullweek: { type: 'fullweek' },
        inkdry: { type: 'inkdry' },
        letter: { type: 'letter' },
        tensouls: { type: 'tensouls' },
        midnight: { type: 'midnight' },
        lastcall: { type: 'lastcall' },
        bookmark: { type: 'bookmark' },
        lantern: { type: 'lantern', title },
        flourish: { type: 'flourish' },
        weave: { type: 'weave' },
        fullmoon: { type: 'fullmoon' },
        oath: { type: 'oath' },
        shelffull: { type: 'shelffull', count: Math.max(projects.length, 5) },
        storm: { type: 'storm' },
        return: { type: 'return' },
        tearpage: { type: 'tearpage' },
        firstword: { type: 'firstword', title },
        scroll: { type: 'scroll', name: 'Marina Duarte' },
        newmoon: { type: 'newmoon' },
        hundredk: { type: 'hundredk' },
        spine10: { type: 'spine10', title },
        record: { type: 'record', words: 1873 },
        monthstars: { type: 'monthstars', nights: 20 },
        cartographer: { type: 'cartographer' },
        fiftycandles: { type: 'fiftycandles' },
        reveal: { type: 'reveal', name: 'Helena' },
        paperlight: { type: 'paperlight' },
        arrived: { type: 'arrived', title },
        planfree: { type: 'plan', plan: 'free' },
        planstarter: { type: 'plan', plan: 'starter' },
        planpro: { type: 'plan', plan: 'professional' },
        planunlimited: { type: 'plan', plan: 'unlimited' },
        hired: { type: 'hired', name: 'Marina Duarte' },
        notifinvite: { type: 'notif', kind: 'invite' },
        notifmention: { type: 'notif', kind: 'mention' },
        notifreview: { type: 'notif', kind: 'review' },
        notifhire: { type: 'notif', kind: 'hire' },
        notifsystem: { type: 'notif', kind: 'system' },
        combo: { type: 'combo', tier: 3, words: 500 },
        combo5: { type: 'combo', tier: 5, words: 1500 },
        flow: { type: 'flow' },
        burst: { type: 'burst', tier: 3 },
        deepflow: { type: 'deepflow' },
        trance: { type: 'trance' },
        mythic: { type: 'mythic' },
        auroranight: { type: 'auroranight' },
        iris: { type: 'iris' },
        fireflies: { type: 'fireflies' },
        twoquills: { type: 'twoquills', email: 'editor@reformu.lat' },
        companion: { type: 'companion', name: 'Marina Duarte' },
        timeback: { type: 'timeback' },
        cleanpage: { type: 'cleanpage' },
        goalset: { type: 'goalset', words: 500 },
        baptism: { type: 'baptism', title: writerTitleOf(writerLevelOf(totalWordsOf(projects)).level + 1, t) },
        booknamed: { type: 'booknamed', title },
        phoenix: { type: 'phoenix' },
        prune: { type: 'prune' },
        earlybird: { type: 'earlybird' },
        twotales: { type: 'twotales' },
        twovigils: { type: 'vigils', n: 2 },
        threevigils: { type: 'vigils', n: 3 },
        archive25: { type: 'archivemilestone', count: 50 },
        thousand: { type: 'thousand' },
        wish: { type: 'wish' },
      };
      if (map[type]) setMoment(map[type]);
      else console.info('playMoment: candle · level · dawn · spark · legendary · streak · vigil · born · crossing · seal · file · burn · unbind · twin · coverswap · feather · fullweek');
    };
    (window as unknown as { playMoment?: typeof play }).playMoment = play;
    return () => {
      window.removeEventListener('reformu:moment', onEvent);
      delete (window as unknown as { playMoment?: unknown }).playMoment;
    };
  }, [projects]);

  // brasinha — primeira palavra do dia, escrita NESTA sessão
  useEffect(() => {
    if (todayWords <= 0 || (sessionStart.current?.words ?? 0) > 0) return;
    const key = `moment-spark-${todayKey()}`;
    if (safeGet(key)) return;
    safeSet(key, '1');
    // ficou uma semana longe? a história esperou.
    const past = (dailyStats || [])
      .filter((d) => d.wordsWritten > 0 && d.date !== todayKey())
      .map((d) => d.date)
      .sort()
      .pop();
    const gapDays = past ? (Date.now() - new Date(past).getTime()) / 86_400_000 : 0;
    // a ofensiva que morreu anteontem era forte? entao e a fenix
    let phoenix = false;
    if (past && gapDays >= 1.5 && gapDays < 7) {
      const days = (dailyStats || []).filter((d) => d.wordsWritten > 0).map((d) => d.date).sort();
      let run = 0;
      const cursor = new Date(past);
      for (let k = days.length - 1; k >= 0; k--) {
        const expected = cursor.toISOString().split('T')[0];
        if (days[k] === expected) { run++; cursor.setDate(cursor.getDate() - 1); }
        else if (days[k] > expected) continue;
        else break;
      }
      phoenix = run >= 7;
    }
    if (phoenix) setMoment({ type: 'phoenix' });
    else if (gapDays >= 7) setMoment({ type: 'return' });
    else setMoment(new Date().getHours() >= 23 ? { type: 'lastcall' } : { type: 'spark' });
  }, [todayWords]);

  // vela — meta do dia cruzada
  useEffect(() => {
    if (todayWords < DAILY_GOAL) return;
    const key = `moment-candle-${todayKey()}`;
    if (safeGet(key)) return;
    safeSet(key, '1');
    const h = new Date().getHours();
    setMoment(h >= 5 && h < 10 ? { type: 'earlybird' } : { type: 'candle' });
  }, [todayWords]);

  // noite lendária — 1.000 cruzadas ao vivo
  useEffect(() => {
    if (todayWords < LEGENDARY_GOAL || (sessionStart.current?.words ?? 0) >= LEGENDARY_GOAL) return;
    const key = `moment-legendary-${todayKey()}`;
    if (safeGet(key)) return;
    safeSet(key, '1');
    setMoment({ type: 'legendary' });
  }, [todayWords]);

  // candelabro — marcos de ofensiva (7 · 30 · 100 noites)
  useEffect(() => {
    if (!dailyStats || dailyStats.length === 0) return;
    const dates = dailyStats
      .filter((d) => d.wordsWritten > 0)
      .map((d) => d.date)
      .sort((a, b) => new Date(b).getTime() - new Date(a).getTime());
    let streak = 0;
    const cursor = new Date();
    for (const date of dates) {
      const expected = cursor.toISOString().split('T')[0];
      if (date === expected) { streak++; cursor.setDate(cursor.getDate() - 1); }
      else if (date === todayKey()) continue;
      else break;
    }
    for (const th of [100, 30, 7]) {
      if (streak === th && !safeGet(`moment-streak-${th}`)) {
        safeSet(`moment-streak-${th}`, '1');
        setMoment({ type: 'streak', nights: th });
        break;
      }
    }
  }, [dailyStats]);

  // vigília — 25min de sessão com escrita
  useEffect(() => {
    const id = setInterval(() => {
      const st = sessionStart.current;
      if (!st) return;
      const key = `moment-vigil-${todayKey()}`;
      if (safeGet(key)) return;
      const grown = (getTodayStats()?.wordsWritten ?? 0) > st.words;
      if (grown && Date.now() - st.at >= VIGIL_MINUTES * 60_000) {
        safeSet(key, '1');
        setMoment({ type: 'vigil' });
      }
    }, 60_000);
    return () => clearInterval(id);
  }, [getTodayStats]);

  // livro nasce — projeto criado agora há pouco
  useEffect(() => {
    for (const p of projects) {
      if (safeGet(`moment-born-${p.id}`)) continue;
      const age = Date.now() - new Date(p.createdAt).getTime();
      safeSet(`moment-born-${p.id}`, '1');
      if (age < 30_000) {
        setMoment({ type: 'born', title: p.metadata.title || '' });
        break;
      }
    }
  }, [projects]);

  // travessia — 25/50/75% cruzados
  useEffect(() => {
    for (const p of projects) {
      const words = p.chapters.reduce((s, c) => s + c.wordCount, 0);
      const pct = (words / NOVEL_TARGET) * 100;
      for (const th of [75, 50, 25]) {
        if (pct >= th && !safeGet(`moment-cross-${p.id}-${th}`)) {
          safeSet(`moment-cross-${p.id}-${th}`, '1');
          setMoment({ type: 'crossing', pct: th, title: p.metadata.title || '' });
          return;
        }
      }
    }
  }, [projects]);

  // amanhecer — manuscrito completo
  useEffect(() => {
    for (const p of projects) {
      const words = p.chapters.reduce((s, c) => s + c.wordCount, 0);
      if (words >= NOVEL_TARGET && !safeGet(`moment-dawn-${p.id}`)) {
        safeSet(`moment-dawn-${p.id}`, '1');
        setMoment({ type: 'dawn', title: p.metadata.title || '' });
        break;
      }
    }
  }, [projects]);

  // semana completa — as 7 noites da semana com escrita
  useEffect(() => {
    if (!dailyStats || dailyStats.length < 7) return;
    const days: string[] = [];
    for (let i = 0; i < 7; i++) {
      const d = new Date();
      d.setDate(d.getDate() - i);
      days.push(d.toISOString().slice(0, 10));
    }
    const all = days.every((day) => dailyStats.some((st) => st.date === day && st.wordsWritten > 0));
    if (!all) return;
    const weekKey = `moment-fullweek-${days[6]}`; // ancorada no 1º dia da janela
    if (safeGet(weekKey)) return;
    safeSet(weekKey, '1');
    setMoment({ type: 'fullweek' });
  }, [dailyStats]);

  // meia-noite virando com o app aberto (e com escrita no dia que acabou)
  const prevHourRef = useRef(new Date().getHours());
  useEffect(() => {
    const id = setInterval(() => {
      const h = new Date().getHours();
      if (prevHourRef.current === 23 && h === 0) {
        const key = `moment-midnight-${todayKey()}`;
        if (!safeGet(key)) { safeSet(key, '1'); setMoment({ type: 'midnight' }); }
      }
      prevHourRef.current = h;
    }, 30_000);
    return () => clearInterval(id);
  }, []);

  // fitilho: saiu do editor tendo escrito nesta visita (1x por dia)
  const location = useLocation();
  const prevPathRef = useRef(location.pathname);
  const editorEnterWordsRef = useRef<number | null>(null);
  useEffect(() => {
    const prev = prevPathRef.current;
    const now = location.pathname;
    prevPathRef.current = now;
    const wasEditor = prev.includes('/app/editor/');
    const isEditor = now.includes('/app/editor/');
    if (!wasEditor && isEditor) {
      editorEnterWordsRef.current = getTodayStats()?.wordsWritten ?? 0;
    }
    if (wasEditor && !isEditor) {
      const entered = editorEnterWordsRef.current;
      editorEnterWordsRef.current = null;
      const nowWords = getTodayStats()?.wordsWritten ?? 0;
      const key = `moment-bookmark-${todayKey()}`;
      if (entered !== null && nowWords > entered && !safeGet(key)) {
        safeSet(key, '1');
        setMoment({ type: 'bookmark' });
      }
    }
  }, [location.pathname, getTodayStats]);

  // estante cheia — o 5º mundo
  useEffect(() => {
    if (projects.length >= 5 && !safeGet('moment-shelffull')) {
      safeSet('moment-shelffull', '1');
      setMoment({ type: 'shelffull', count: projects.length });
    }
  }, [projects]);

  // a primeira palavra de um livro (novo)
  useEffect(() => {
    for (const p of projects) {
      const words = p.chapters.reduce((sum, c) => sum + c.wordCount, 0);
      if (words > 0 && !safeGet(`moment-firstword-${p.id}`)) {
        safeSet(`moment-firstword-${p.id}`, '1');
        setMoment({ type: 'firstword', title: p.metadata.title || '' });
        break;
      }
    }
  }, [projects]);

  // lua cheia de verdade — uma vez por noite de lua cheia
  useEffect(() => {
    const SYNODIC = 29.53058867;
    const known = Date.UTC(2000, 0, 6, 18, 14); // lua nova de referência
    const phase = (((Date.now() - known) / 86_400_000) % SYNODIC) / SYNODIC;
    if (phase < 0.47 || phase > 0.53) return;
    const key = `moment-fullmoon-${todayKey()}`;
    if (safeGet(key)) return;
    safeSet(key, '1');
    setMoment({ type: 'fullmoon' });
  }, []);

  // o pacto — primeira vez que o escritor entra no editor (uma vez na vida)
  useEffect(() => {
    if (!location.pathname.includes('/app/editor/')) return;
    if (safeGet('moment-oath')) return;
    safeSet('moment-oath', '1');
    setMoment({ type: 'oath' });
  }, [location.pathname]);

  // mudança de plano — uma cerimônia diferente por plano
  const plan = useSubscriptionStore((st) => st.plan);
  const prevPlanRef = useRef<PlanType | null>(null);
  useEffect(() => {
    const prev = prevPlanRef.current;
    prevPlanRef.current = plan;
    if (prev === null || prev === plan) return; // baseline silencioso
    setMoment({ type: 'plan', plan });
  }, [plan]);

  // combo — marcos de palavras DENTRO da sessão, intensidade crescente
  const comboFiredRef = useRef<Set<number>>(new Set());
  useEffect(() => {
    const st = sessionStart.current;
    if (!st) return;
    const sessionWords = todayWords - st.words;
    const TIERS: Array<[number, 1 | 2 | 3 | 4 | 5]> = [[1500, 5], [750, 4], [500, 3], [250, 2], [100, 1]];
    for (const [threshold, tier] of TIERS) {
      if (sessionWords >= threshold && !comboFiredRef.current.has(threshold)) {
        comboFiredRef.current.add(threshold);
        setMoment({ type: 'combo', tier, words: threshold });
        break;
      }
    }
  }, [todayWords]);

  // fluxo — 10 minutos de escrita contínua (checagens de 2min com crescimento)
  const flowRef = useRef({ lastWords: 0, streak: 0, fired: false });
  useEffect(() => {
    const id = setInterval(() => {
      const f = flowRef.current;
      if (f.fired) return;
      const words = getTodayStats()?.wordsWritten ?? 0;
      if (words - f.lastWords >= 20) f.streak += 1;
      else f.streak = 0;
      f.lastWords = words;
      if (f.streak >= 5) {
        f.fired = true;
        setMoment({ type: 'flow' });
      }
    }, 120_000);
    return () => clearInterval(id);
  }, [getTodayStats]);

  // lua nova — céu escuro, só as suas estrelas (mesma matemática da lua cheia)
  useEffect(() => {
    const SYNODIC = 29.53058867;
    const known = Date.UTC(2000, 0, 6, 18, 14);
    const phase = (((Date.now() - known) / 86_400_000) % SYNODIC) / SYNODIC;
    if (phase > 0.035 && phase < 0.965) return;
    const key = `moment-newmoon-${todayKey()}`;
    if (safeGet(key)) return;
    safeSet(key, '1');
    setMoment({ type: 'newmoon' });
  }, []);

  // cem mil palavras do escritor (todas as obras)
  useEffect(() => {
    if (projects.length === 0) return;
    if (totalWordsOf(projects) >= 100_000 && !safeGet('moment-100k')) {
      safeSet('moment-100k', '1');
      setMoment({ type: 'hundredk' });
    }
  }, [projects]);

  // dez capítulos num livro
  useEffect(() => {
    for (const p of projects) {
      if (p.chapters.length >= 10 && !safeGet(`moment-spine10-${p.id}`)) {
        safeSet(`moment-spine10-${p.id}`, '1');
        setMoment({ type: 'spine10', title: p.metadata.title || '' });
        break;
      }
    }
  }, [projects]);

  // recorde pessoal — melhor noite superada AO VIVO
  useEffect(() => {
    if (!dailyStats || todayWords <= 0) return;
    const prevBest = Math.max(
      0,
      ...dailyStats.filter((d) => d.date !== todayKey()).map((d) => d.wordsWritten)
    );
    if (prevBest < 500) return; // sem histórico digno, sem recorde
    if (todayWords <= prevBest) return;
    if ((sessionStart.current?.words ?? 0) > prevBest) return; // já era recorde ao abrir
    const key = `moment-record-${todayKey()}`;
    if (safeGet(key)) return;
    safeSet(key, '1');
    setMoment({ type: 'record', words: todayWords });
  }, [todayWords, dailyStats]);

  // mês constelado — 20 noites de escrita no mês corrente
  useEffect(() => {
    if (!dailyStats) return;
    const month = todayKey().slice(0, 7);
    const nights = dailyStats.filter((d) => d.date.startsWith(month) && d.wordsWritten > 0).length;
    if (nights < 20) return;
    const key = `moment-monthstars-${month}`;
    if (safeGet(key)) return;
    safeSet(key, '1');
    setMoment({ type: 'monthstars', nights });
  }, [dailyStats]);

  // o velário — 50 noites (na vida) com a meta batida
  useEffect(() => {
    if (!dailyStats) return;
    const lit = dailyStats.filter((d) => d.wordsWritten >= DAILY_GOAL).length;
    if (lit >= 50 && !safeGet('moment-50candles')) {
      safeSet('moment-50candles', '1');
      setMoment({ type: 'fiftycandles' });
    }
  }, [dailyStats]);

  // a poda — a sessão CORTOU 300+ palavras do total (edição corajosa)
  const sessionTotalRef = useRef<number | null>(null);
  const perProjectStartRef = useRef<Map<string, number> | null>(null);
  useEffect(() => {
    const total = totalWordsOf(projects);
    if (sessionTotalRef.current === null && projects.length > 0) {
      sessionTotalRef.current = total;
      perProjectStartRef.current = new Map(
        projects.map((p) => [p.id, p.chapters.reduce((sum, c) => sum + c.wordCount, 0)])
      );
      return;
    }
    const start = sessionTotalRef.current;
    if (start === null) return;
    const key = `moment-prune-${todayKey()}`;
    if (total <= start - 300 && !safeGet(key)) {
      safeSet(key, '1');
      setMoment({ type: 'prune' });
    }
    // pena dupla — 2+ livros cresceram nesta sessão
    const starts = perProjectStartRef.current;
    if (starts) {
      const grown = projects.filter((p) => {
        const st = starts.get(p.id);
        if (st === undefined) return false;
        return p.chapters.reduce((sum, c) => sum + c.wordCount, 0) >= st + 50;
      }).length;
      const key2 = `moment-twotales-${todayKey()}`;
      if (grown >= 2 && !safeGet(key2)) {
        safeSet(key2, '1');
        setMoment({ type: 'twotales' });
      }
    }
  }, [projects]);

  // devoção — três sessões de escrita no mesmo dia
  useEffect(() => {
    const today = todayKey();
    const count = projects.reduce(
      (n, p) => n + (p.sessions || []).filter((ss) => String(ss.startTime).slice(0, 10) === today).length,
      0
    );
    if (count >= 3 && !safeGet(`moment-threevigils-${today}`)) {
      safeSet(`moment-threevigils-${today}`, '1');
      setMoment({ type: 'vigils', n: 3 });
    } else if (count === 2 && !safeGet(`moment-twovigils-${today}`)) {
      safeSet(`moment-twovigils-${today}`, '1');
      setMoment({ type: 'vigils', n: 2 });
    }
  }, [projects]);

  // batismo do livro — o título deixou de ser vazio/"Sem título"
  useEffect(() => {
    for (const p of projects) {
      const title = (p.metadata.title || '').trim();
      if (!title || /^sem t[ií]tulo$|^untitled$/i.test(title)) continue;
      if (safeGet(`moment-booknamed-${p.id}`)) continue;
      safeSet(`moment-booknamed-${p.id}`, '1');
      setMoment({ type: 'booknamed', title });
      break;
    }
  }, [projects]);

  // lamparina — o livro completa 30 dias de jornada (janela de 1 dia)
  useEffect(() => {
    for (const p of projects) {
      const ageDays = (Date.now() - new Date(p.createdAt).getTime()) / 86_400_000;
      if (ageDays >= 30 && ageDays < 31 && !safeGet(`moment-lantern30-${p.id}`)) {
        safeSet(`moment-lantern30-${p.id}`, '1');
        setMoment({ type: 'lantern', title: p.metadata.title || '' });
        break;
      }
    }
  }, [projects]);

  // level up
  useEffect(() => {
    if (projects.length === 0) return;
    const seen = Number(safeGet('moment-level-seen') || '0');
    if (seen === 0) { safeSet('moment-level-seen', String(level)); return; }
    if (level > seen) {
      safeSet('moment-level-seen', String(level));
      // título novo = batismo (cerimônia maior); senão, level up comum
      const prevTitle = writerTitleOf(seen, t);
      const newTitle = writerTitleOf(level, t);
      if (newTitle !== prevTitle) setMoment({ type: 'baptism', title: newTitle });
      else setMoment({ type: 'level', level });
    }
  }, [level, projects.length]);

  // ofensiva atual (mesma matemática do candelabro) — alimenta o fio de brasa
  const streakNow = useMemo(() => {
    if (!dailyStats || dailyStats.length === 0) return 0;
    const dates = dailyStats
      .filter((d) => d.wordsWritten > 0)
      .map((d) => d.date)
      .sort((a, b) => new Date(b).getTime() - new Date(a).getTime());
    let streak = 0;
    const cursor = new Date();
    for (const date of dates) {
      const expected = cursor.toISOString().split('T')[0];
      if (date === expected) { streak++; cursor.setDate(cursor.getDate() - 1); }
      else if (date === todayKey()) continue;
      else break;
    }
    return streak;
  }, [dailyStats]);

  // rajada — velocidade de escrita em janelas de 30s (tiers por janelas seguidas)
  const burstRef = useRef({ lastWords: 0, hot: 0, cooldownAt: 0 });
  useEffect(() => {
    const id = setInterval(() => {
      const b = burstRef.current;
      const words = getTodayStats()?.wordsWritten ?? 0;
      const delta = words - b.lastWords;
      b.lastWords = words;
      if (delta >= 40) {
        b.hot = Math.min(b.hot + 1, 3);
        if (Date.now() - b.cooldownAt > 150_000) {
          b.cooldownAt = Date.now();
          setMoment({ type: 'burst', tier: b.hot as 1 | 2 | 3 });
        }
      } else if (delta <= 0) {
        b.hot = 0;
      }
    }, 30_000);
    return () => clearInterval(id);
  }, [getTodayStats]);

  // fluxo profundo (30min) e transe (60min) — continuação do contador do fluxo
  useEffect(() => {
    const id = setInterval(() => {
      const f = flowRef.current as unknown as { streak: number; deep?: boolean; trance?: boolean };
      if (f.streak >= 30 && !f.trance) { f.trance = true; setMoment({ type: 'trance' }); return; }
      if (f.streak >= 15 && !f.deep) { f.deep = true; setMoment({ type: 'deepflow' }); }
    }, 120_000);
    return () => clearInterval(id);
  }, []);

  // noites escalonadas — 2.000 (mítica) e 3.000 (aurora), cruzadas ao vivo
  useEffect(() => {
    const startWords = sessionStart.current?.words ?? 0;
    for (const [goal, type] of [[3000, 'auroranight'], [2000, 'mythic']] as Array<[number, 'mythic' | 'auroranight']>) {
      if (todayWords < goal || startWords >= goal) continue;
      const key = `moment-${type}-${todayKey()}`;
      if (safeGet(key)) continue;
      safeSet(key, '1');
      setMoment({ type });
      break;
    }
  }, [todayWords]);

  // vaga-lumes — TODA vez que a Home é aberta (pedido do usuário: "sempre que
  // entramos na home"). Replay a cada visita (não só na 1ª): dispara ao ENTRAR
  // em "/app" (o mount inicial nela conta como entrada), nunca em loop enquanto
  // a página fica aberta — a comparação com a ref evita disparo duplo por
  // re-render. Sem gate de horário: os brilhos sobem em qualquer hora.
  const wasHomeRef = useRef(false);
  useEffect(() => {
    const isHome = location.pathname === '/app';
    const enteredHome = isHome && !wasHomeRef.current;
    wasHomeRef.current = isHome;
    if (!enteredHome) return;
    setMoment({ type: 'fireflies' });
  }, [location.pathname]);

  const done = () => {
    playingRef.current = null;
    setMomentState(null);
    // respiro entre cenas; depois puxa a próxima da fila
    setTimeout(() => {
      const next = queueRef.current.shift();
      if (next && !playingRef.current) {
        playingRef.current = next;
        setMomentState(next);
      }
    }, 650);
  };

  return (
    <>
    <StreakEmberLine streak={streakNow} />
    <AnimatePresence>
      {moment?.type === 'candle' && <CandleMoment key="m-candle" onDone={done} />}
      {moment?.type === 'level' && <LevelUpMoment key="m-level" level={moment.level} onDone={done} />}
      {moment?.type === 'dawn' && <DawnMoment key="m-dawn" title={moment.title} onDone={done} />}
      {moment?.type === 'spark' && <SparkMoment key="m-spark" onDone={done} />}
      {moment?.type === 'legendary' && <LegendaryMoment key="m-legendary" onDone={done} />}
      {moment?.type === 'streak' && <StreakMoment key="m-streak" nights={moment.nights} onDone={done} />}
      {moment?.type === 'vigil' && <VigilMoment key="m-vigil" onDone={done} />}
      {moment?.type === 'born' && <BornMoment key="m-born" title={moment.title} onDone={done} />}
      {moment?.type === 'crossing' && <CrossingMoment key="m-crossing" pct={moment.pct} title={moment.title} onDone={done} />}
      {moment?.type === 'seal' && <SealMoment key="m-seal" title={moment.title} onDone={done} />}
      {moment?.type === 'file' && <FiledMoment key="m-file" name={moment.name} label={moment.label} onDone={done} />}
      {moment?.type === 'burn' && <BurnMoment key="m-burn" name={moment.name} onDone={done} />}
      {moment?.type === 'unbind' && <UnbindMoment key="m-unbind" title={moment.title} onDone={done} />}
      {moment?.type === 'twin' && <TwinMoment key="m-twin" title={moment.title} onDone={done} />}
      {moment?.type === 'coverswap' && <CoverSwapMoment key="m-coverswap" title={moment.title} img={moment.img} onDone={done} />}
      {moment?.type === 'feather' && <FeatherMoment key="m-feather" onDone={done} />}
      {moment?.type === 'fullweek' && <FullWeekMoment key="m-fullweek" onDone={done} />}
      {moment?.type === 'inkdry' && <InkDryMoment key="m-inkdry" onDone={done} />}
      {moment?.type === 'letter' && <LetterMoment key="m-letter" onDone={done} />}
      {moment?.type === 'tensouls' && <TenSoulsMoment key="m-tensouls" onDone={done} />}
      {moment?.type === 'midnight' && <MidnightMoment key="m-midnight" onDone={done} />}
      {moment?.type === 'lastcall' && <LastCallMoment key="m-lastcall" onDone={done} />}
      {moment?.type === 'bookmark' && <BookmarkMoment key="m-bookmark" onDone={done} />}
      {moment?.type === 'lantern' && <LanternMoment key="m-lantern" title={moment.title} onDone={done} />}
      {moment?.type === 'flourish' && <FlourishMoment key="m-flourish" onDone={done} />}
      {moment?.type === 'weave' && <WeaveMoment key="m-weave" onDone={done} />}
      {moment?.type === 'fullmoon' && <FullMoonMoment key="m-fullmoon" onDone={done} />}
      {moment?.type === 'oath' && <OathMoment key="m-oath" onDone={done} />}
      {moment?.type === 'shelffull' && <ShelfFullMoment key="m-shelffull" count={moment.count} onDone={done} />}
      {moment?.type === 'storm' && <StormMoment key="m-storm" onDone={done} />}
      {moment?.type === 'return' && <ReturnMoment key="m-return" onDone={done} />}
      {moment?.type === 'tearpage' && <TearPageMoment key="m-tearpage" onDone={done} />}
      {moment?.type === 'firstword' && <FirstWordMoment key="m-firstword" title={moment.title} onDone={done} />}
      {moment?.type === 'scroll' && <ScrollMoment key="m-scroll" name={moment.name} onDone={done} />}
      {moment?.type === 'newmoon' && <NewMoonMoment key="m-newmoon" onDone={done} />}
      {moment?.type === 'hundredk' && <HundredKMoment key="m-hundredk" onDone={done} />}
      {moment?.type === 'spine10' && <Spine10Moment key="m-spine10" title={moment.title} onDone={done} />}
      {moment?.type === 'record' && <RecordMoment key="m-record" words={moment.words} onDone={done} />}
      {moment?.type === 'monthstars' && <MonthStarsMoment key="m-monthstars" nights={moment.nights} onDone={done} />}
      {moment?.type === 'cartographer' && <CartographerMoment key="m-cartographer" onDone={done} />}
      {moment?.type === 'fiftycandles' && <FiftyCandlesMoment key="m-fiftycandles" onDone={done} />}
      {moment?.type === 'reveal' && <RevealMoment key="m-reveal" name={moment.name} img={moment.img} onDone={done} />}
      {moment?.type === 'paperlight' && <PaperLightMoment key="m-paperlight" onDone={done} />}
      {moment?.type === 'arrived' && <ArrivedMoment key="m-arrived" title={moment.title} onDone={done} />}
      {moment?.type === 'plan' && moment.plan === 'free' && <PlanFreeMoment key="m-plan" onDone={done} />}
      {moment?.type === 'plan' && moment.plan === 'starter' && <PlanStarterMoment key="m-plan" onDone={done} />}
      {moment?.type === 'plan' && moment.plan === 'professional' && <PlanProfessionalMoment key="m-plan" onDone={done} />}
      {moment?.type === 'plan' && moment.plan === 'unlimited' && <PlanUnlimitedMoment key="m-plan" onDone={done} />}
      {moment?.type === 'hired' && <HiredMoment key="m-hired" name={moment.name} onDone={done} />}
      {moment?.type === 'notif' && <NotifCardMoment key={`m-notif-${moment.kind}`} kind={moment.kind} onDone={done} />}
      {moment?.type === 'combo' && <ComboMoment key={`m-combo-${moment.tier}`} tier={moment.tier} words={moment.words} onDone={done} />}
      {moment?.type === 'flow' && <FlowMoment key="m-flow" onDone={done} />}
      {moment?.type === 'burst' && <BurstMoment key={`m-burst-${moment.tier}`} tier={moment.tier} onDone={done} />}
      {moment?.type === 'deepflow' && <DeepFlowMoment key="m-deepflow" onDone={done} />}
      {moment?.type === 'trance' && <TranceMoment key="m-trance" onDone={done} />}
      {moment?.type === 'mythic' && <MythicNightMoment key="m-mythic" onDone={done} />}
      {moment?.type === 'auroranight' && <AuroraNightMoment key="m-auroranight" onDone={done} />}
      {moment?.type === 'iris' && <IrisMoment key="m-iris" onDone={done} />}
      {moment?.type === 'fireflies' && <FirefliesMoment key="m-fireflies" onDone={done} />}
      {moment?.type === 'twoquills' && <TwoQuillsMoment key="m-twoquills" email={moment.email} onDone={done} />}
      {moment?.type === 'companion' && <CompanionMoment key="m-companion" name={moment.name} onDone={done} />}
      {moment?.type === 'timeback' && <TimeBackMoment key="m-timeback" onDone={done} />}
      {moment?.type === 'cleanpage' && <CleanPageMoment key="m-cleanpage" onDone={done} />}
      {moment?.type === 'goalset' && <GoalSetMoment key="m-goalset" words={moment.words} onDone={done} />}
      {moment?.type === 'baptism' && <BaptismMoment key="m-baptism" title={moment.title} onDone={done} />}
      {moment?.type === 'booknamed' && <BookNamedMoment key="m-booknamed" title={moment.title} onDone={done} />}
      {moment?.type === 'phoenix' && <PhoenixMoment key="m-phoenix" onDone={done} />}
      {moment?.type === 'prune' && <PruneMoment key="m-prune" onDone={done} />}
      {moment?.type === 'earlybird' && <EarlyBirdMoment key="m-earlybird" onDone={done} />}
      {moment?.type === 'twotales' && <TwoTalesMoment key="m-twotales" onDone={done} />}
      {moment?.type === 'vigils' && <ThreeVigilsMoment key={`m-vigils-${moment.n}`} n={moment.n} onDone={done} />}
      {moment?.type === 'archivemilestone' && <ArchiveFullMoment key={`m-archive-${moment.count}`} count={moment.count} onDone={done} />}
      {moment?.type === 'thousand' && <ThousandMoment key="m-thousand" onDone={done} />}
      {moment?.type === 'wish' && <WishMoment key="m-wish" onDone={done} />}
    </AnimatePresence>
    </>
  );
}
